/**
 * AVBP Project + Client persistence — the live record layer.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY (task #888 — Convert must write real records; kill DEMO_PID)
 * ─────────────────────────────────────────────────────────────────────────
 *  Before this, "Convert to Project" only mutated React state and the
 *  Clients/Projects/Conversation screens read static `DEMO_PROJECTS` /
 *  `DEMO_PID`. A converted lead never became a real project, so a refresh
 *  erased it and every staffer's conversation collided on `projects/VP656`.
 *
 *  This module:
 *    • mints the C##### client number from a real Firestore counter
 *      (the `counters/client_*` pattern the rules already permit team to
 *      transact — see firestore.rules counters block) — the counter is
 *      reseeded to continue the owner's live `C#####` client series;
 *    • mints a stable VP project id;
 *    • writes a real `projects/{pid}` doc EXACTLY like `ensureDemoProject`
 *      (same templateVersion lock + empty gate state + creation ledger
 *      entry) so every existing Step-6 screen works unchanged;
 *    • writes a real `clients/{cid}` master record;
 *    • lists live `projects/` so Clients/ProjectDetail read real data.
 *
 *  NO Firestore-rules change. `projects` + `clients` + `counters/client_*`
 *  team create/read/write are already allowed by the live prod rules.
 *  Reuses the PURE `convertLeadToProject` bridge from leads.ts (the locked
 *  pricing + first money gate are computed there, not reinvented here).
 *
 *  PURE/IMPURE split: `clientNoFromSeq` / `projectIdFromSeq` /
 *  `projectDocFromSeed` are PURE + unit-tested. Only the counter
 *  transaction + doc writes + the live listener touch Firestore.
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  addDoc,
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  ENGAGEMENT_TEMPLATE_VERSION,
  emptyGateState,
} from './projectLedger';
import {
  autoQuote,
  convertLeadDirect,
  type ConvertResult,
  type Lead,
} from './leads';
import {
  getEngagementTemplate,
  type CoreTier,
  type Tier,
} from './engagementTemplate';
import type { ParticipantEntry, PlaceOfBirth } from './contactModel';
import type { PortalRole, PortalSection } from './visibilityModel';
import { getLead, updateLead } from './leadStore';
import { firstOpenSentQuoteId } from './leadQuote';

/**
 * PURE — shallow-strip `undefined` values from a plain data object. Firestore
 * rejects `undefined` field values (this client is not initialised with
 * `ignoreUndefinedProperties`), so any optional field left blank must be dropped
 * before a write. `null` is preserved (an intentional "clear this field"). Only
 * plain data objects should be passed — never a payload carrying `serverTimestamp()`
 * or other FieldValue sentinels.
 */
function pruneUndefined<T extends object>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as T;
}

/* ════════════════════ Pure id mints (locked formats) ════════════════════ */

/**
 * C##### — the client-number format. OWNER DECISION 2026-05-22: new clients
 * MUST continue the owner's existing live `C#####` series, NOT a fresh
 * `AV-C-####` band.
 *
 *  Why this changed:  the prior `AV-C-####` mint was a portal-only invention
 *  (4 stray `AV-C-100x` test docs are its only footprint). The owner's 465
 *  real clients all use `C` + a 5-digit zero-padded serial as their
 *  `clients/{id}` doc id (`C00001` … `C00466`). A live read-only scan of the
 *  full `clients` collection (484 docs) on 2026-05-22 found the true highest
 *  to be `C00466`; so `counters/client_av` was reseeded to `seq:466` and the
 *  next minted client is `C00467`. The counter is max-based, so it never
 *  reuses the one historical gap (`C00376`) — no collision is possible.
 *
 *  Format is EXACT, matched to live data — `C` prefix, 5 digits, zero-pad.
 *  Do not change the width without re-scanning the live collection.
 */
export function clientNoFromSeq(seq: number): string {
  return `C${String(seq).padStart(5, '0')}`;
}

/**
 * AV-P-#### — the partner-number format. PARTNER SPRINT 2026-05-21
 * (AVBP_DATA_MODEL_IA_SPEC_v1 §2 / Q6). A partner is an enduring party,
 * so it gets an INDEPENDENT 4-digit-padded id scheme from its own
 * `counters/partner_av` counter (seeded at 1000 → first partner is
 * `AV-P-1001`), so partner numbers and client numbers never collide and
 * never couple. NOTE: this is deliberately a PORTAL-NATIVE scheme — unlike
 * the client number (which 2026-05-22 was switched to continue the owner's
 * pre-existing live `C#####` series), partners have no legacy series to
 * continue, so the `AV-P-####` band stands.
 */
export function partnerNoFromSeq(seq: number): string {
  return `AV-P-${String(seq).padStart(4, '0')}`;
}

/**
 * VP project id — `VP` + zero-padded to (at least) 3 digits, matching the
 * owner's live `projects` collection (595 docs run `VP004`…`VP665`, all
 * 3-digit zero-padded).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  OWNER DECISION 2026-05-22 — the project number must CONTINUE the live
 *  VP series, not start a new band. A read-only Admin-SDK scan of the full
 *  live `projects` collection found the true max real project = `VP665`
 *  (3-digit). The prior code minted `VP${seq}` from a 1000-seeded counter,
 *  producing the stray off-format `VP1001` — the one project that broke the
 *  series. `counters/project_av` was reseeded live to `seq:665`, so the next
 *  mint is `seq:666` → `VP666` — max-continuation, no gap reuse, no
 *  collision with any existing project.
 *
 *  IA FIX 2026-05-21 — Client# and Project# are INDEPENDENT sequences
 *  (AVBP_DATA_MODEL_IA_SPEC_v1 §4 / Q1). Pre-IA-fix, `convertLeadPersist`
 *  minted ONE `client_av` seq and used it for BOTH numbers — hard-wiring
 *  them 1:1 forever. The project counter is now its own `counters/
 *  project_av` doc; minting a project for an EXISTING client mints a fresh
 *  `VP###` while the existing `C#####` is untouched.
 * ─────────────────────────────────────────────────────────────────────────
 */
export function projectIdFromSeq(seq: number): string {
  return `VP${String(seq).padStart(3, '0')}`;
}

/* ════════════════════ Pure project-doc builder ════════════════════ */

/**
 * The `projects/{pid}` doc body — byte-for-byte the shape
 * `ensureDemoProject` writes (so getProjectMeta / the gate engine / the
 * ledger all consume it unchanged), plus the fields the live
 * Clients/ProjectDetail list needs (tier code, assignee, value,
 * activeStageKey). Pure: `createdAt` is filled with serverTimestamp by the
 * impure caller, not here.
 *
 * IA FIX 2026-05-21 — every project carries `clientId` (the SOURCE OF
 * TRUTH link to `clients/{id}`, AVBP_DATA_MODEL_IA_SPEC_v1 §4) and a
 * nullable `partnerId` (defined now, Partner Portfolio built in the later
 * Partner sprint — spec Q6). `clientName`/`clientNo` are kept as a display
 * cache but `clientId` is authoritative. `clientId` defaults to the
 * `clientNo` (the convert path uses the client number as the `clients/{id}`
 * doc id) when not supplied explicitly.
 */
export function projectDocFromSeed(
  seed: ConvertResult['projectSeed'],
  assigneeUid: string | null,
  assigneeName: string,
  clientId?: string,
  partnerId?: string | null,
  /**
   * IA / name-drift fix — the project's `clientName` cache. When the lead
   * is converted onto an EXISTING client, the cache MUST be the existing
   * client's master name, NOT the lead's name (a lead-named cache that
   * differs from the master is exactly the C00464 drift). Defaults to
   * `seed.clientName` (= the lead name) for a brand-new client, where the
   * master is created from the same lead name.
   */
  clientName?: string,
  /**
   * The project's own human-readable NAME (2026-05-22). User-settable at
   * creation; distinct from the client name and the `VP###` code. When
   * omitted/blank a sensible default `<tier> — <client name>` is written so
   * the project is never nameless, but the create UIs always pass a value.
   */
  projectName?: string,
): Record<string, unknown> {
  const resolvedClientName = clientName ?? seed.clientName;
  const trimmedName = String(projectName ?? '').trim();
  return {
    vp: seed.vp,
    // The project's own name — defaults to `<tier> — <client name>` when the
    // caller passes nothing, so a project doc is never written nameless.
    projectName:
      trimmedName || `${seed.tier} — ${resolvedClientName}`,
    clientName: clientName ?? seed.clientName,
    clientNo: seed.clientNo,
    // IA FIX — the real reference to clients/{id}. The convert path uses
    // the client number as the clients/ doc id, so it is also the clientId
    // unless an explicit existing-client id is supplied.
    clientId: clientId ?? seed.clientNo,
    // Nullable channel-partner reference (Partner sprint builds the UI).
    partnerId: partnerId ?? null,
    // Human "tier · displayName" (matches the DEMO_META.tier shape that
    // getProjectMeta returns) so the Conversation header reads right…
    tier: `${seed.tier} · ${seed.displayName}`,
    // …and the raw tier CODE for the engagementTemplate-driven screens.
    tierCode: seed.tier as Tier,
    displayName: seed.displayName,
    stage: 'Intake',
    activeStageKey: seed.initialStageKey,
    valueINR: seed.valueINR,
    assigneeUid: assigneeUid ?? 'unassigned',
    assigneeName: assigneeName || 'Unassigned',
    clearedGateIds: [] as string[],
    firstMoneyGateId: seed.firstMoneyGateId,
    templateVersion: ENGAGEMENT_TEMPLATE_VERSION,
    gateState: emptyGateState(),
    // AGREED-PRICE — "whatever price you agree IS what the client pays". The
    // seed carries the agreed currency/firm/GST + the per-gate `gateAmounts`
    // override (seeded from the agreed value via `seedGateAmounts`). Writing
    // them HERE means the money gate on the freshly-converted project expects
    // the agreed figure ($40) from the instant it exists — the canonical
    // `resolveGateAmount` reads `gateAmounts`. An un-priced/standing-rate
    // convert seeds INR + empty gateAmounts (the template default applies).
    currency: seed.currency,
    gst: seed.gst,
    firmId: seed.firmId,
    gateAmounts: seed.gateAmounts,
    trackedInPayments: true,
    seededBy: 'front-door-convert',
    source: 'lead_convert',
  };
}

/* ════════════════════ Counter mint (impure, rules-safe) ════════════════════ */

/**
 * Atomically mint the next client serial from `counters/client_av`. The
 * rules permit team to write counters whose id matches `client_.*` — this
 * is exactly the Sprint-2 client-id transaction pattern referenced in the
 * rules comment. Returns the new integer seq.
 *
 *  RESEEDED 2026-05-22 to continue the owner's live `C#####` series. A
 *  read-only scan of the full live `clients` collection found the highest
 *  existing serial to be `C00466`, so `counters/client_av` was set to
 *  `seq:466` — the next mint is `seq:467` → `C00467`. The transaction is
 *  max-continuation: it always increments the stored counter, so even if
 *  the counter doc were ever lost the fallback below keeps a new mint above
 *  the live range (the genuine recovery path is to re-run the reseed scan).
 */
export async function nextClientSeq(): Promise<number> {
  const ref = doc(db, 'counters', 'client_av');
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur =
      snap.exists() && typeof snap.data().seq === 'number'
        ? (snap.data().seq as number)
        : 466;
    const next = cur + 1;
    tx.set(ref, { seq: next, updatedAt: serverTimestamp() }, { merge: true });
    return next;
  });
}

/**
 * IA FIX 2026-05-21 — atomically mint the next PROJECT serial from
 * `counters/project_av`, an INDEPENDENT counter from `client_av` (spec Q1:
 * the two number sequences are decoupled). The rules permit team to write
 * `counters/.*` whose id matches the counter pattern — `project_av` is a
 * counter doc under the same permitted block.
 *
 *  RESEEDED 2026-05-22 to continue the owner's live `VP###` series. A
 *  read-only scan of the full live `projects` collection found the highest
 *  real project to be `VP665` (the prior 1000-seed had minted one stray
 *  `VP1001`). `counters/project_av` was reseeded to `seq:665`, so the next
 *  mint is `seq:666` → `VP666`. The transaction is max-continuation: it
 *  always increments the stored counter, so the fallback below only matters
 *  if the counter doc were ever lost — and it keeps a new mint at the live
 *  max+1 (the genuine recovery path is to re-run the reseed scan).
 *
 * BEFORE the IA fix, the project id was `projectIdFromSeq(clientSeq)` — the
 * same seq as the client, hard-coupling C#####↔VP### 1:1. After: a new
 * project for an existing client gets its OWN fresh VP### here.
 */
async function nextProjectSeq(): Promise<number> {
  const ref = doc(db, 'counters', 'project_av');
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur =
      snap.exists() && typeof snap.data().seq === 'number'
        ? (snap.data().seq as number)
        : 665;
    const next = cur + 1;
    tx.set(ref, { seq: next, updatedAt: serverTimestamp() }, { merge: true });
    return next;
  });
}

/**
 * PARTNER SPRINT 2026-05-21 — atomically mint the next PARTNER serial from
 * `counters/partner_av`, an INDEPENDENT counter (a partner is its own
 * enduring entity — spec §2). Seeded at 1000, same band rule as the client
 * and project counters. The rules permit team to write `counters/.*` whose
 * id matches the counter pattern — `partner_av` is a new counter doc under
 * the same permitted block (no firestore.rules change). Returns the new
 * integer seq.
 */
async function nextPartnerSeq(): Promise<number> {
  const ref = doc(db, 'counters', 'partner_av');
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur =
      snap.exists() && typeof snap.data().seq === 'number'
        ? (snap.data().seq as number)
        : 1000;
    const next = cur + 1;
    tx.set(ref, { seq: next, updatedAt: serverTimestamp() }, { merge: true });
    return next;
  });
}

/* ════════════════════ Convert → real records ════════════════════ */

export interface ConvertPersistResult {
  /** The transitioned (converted) pure lead — caller persists it back. */
  lead: Lead;
  projectId: string;
  clientNo: string;
  /** The clients/{id} doc id the project was attached to. */
  clientId: string;
  /** true → a brand-new client master was created by this convert. */
  newClientCreated: boolean;
}

/**
 * IA FIX 2026-05-21 — how a lead conversion attaches to a client.
 *
 * Spec §3 / Q7: converting a lead creates one project, attached either to
 *  - a BRAND-NEW client (`{ mode: 'new' }`), OR
 *  - an EXISTING client (`{ mode: 'existing', clientId, clientNo }`) — the
 *    converter picks the client by number or name search in the UI.
 *
 * `mode: 'new'` is the legacy default behaviour (mint a fresh C#####).
 */
export type ConvertClientTarget =
  | { mode: 'new' }
  | {
      mode: 'existing';
      clientId: string;
      clientNo: string;
      /**
       * The existing client's CANONICAL name (from the clients/{id}
       * master). The new project's `clientName` cache is seeded from this,
       * NOT from the lead — so converting a lead onto an existing client
       * never drifts that client's name. Optional for back-compat; when
       * omitted the project keeps the lead name (the pre-fix behaviour).
       */
      clientName?: string;
    };

/**
 * IMPURE — the first OPEN (status 'sent') quote raised from a lead, or null.
 * One-shot equality query (leadId + status — served by single-field indexes,
 * no composite index needed); the pure filter (`firstOpenSentQuoteId`,
 * leadQuote.ts) does the exclusion so it stays unit-testable. Used by the
 * convert guard below and offered to the UI gate (`convertGate`).
 */
export async function findOpenSentQuoteIdForLead(
  leadId: string,
  excludeQuoteId: string | null = null,
): Promise<string | null> {
  const snap = await getDocs(
    query(
      collection(db, 'quotes'),
      where('leadId', '==', leadId),
      where('status', '==', 'sent'),
    ),
  );
  const rows = snap.docs.map((d) => ({
    id: d.id,
    status: String((d.data() as Record<string, unknown>).status ?? ''),
  }));
  return firstOpenSentQuoteId(rows, excludeQuoteId);
}

/**
 * IMPURE — the project a (possibly crashed) conversion run already minted for
 * this lead, or null. Reads the `sourceLeadId` back-ref stamped at project
 * creation (equality query — single-field index, no composite needed). The
 * accept→convert orchestrator calls this AFTER winning the conversion claim
 * and BEFORE minting, so a retry of a partially-failed run RESUMES the
 * existing project instead of creating a second one (Vex gate F2). Projects
 * created before 2026-07-03 carry no back-ref — for them this returns null,
 * which only matters for in-flight crash recovery (impossible for old docs).
 */
export async function findConvertedProjectForLead(
  leadId: string,
): Promise<{ projectId: string; clientNo: string; clientId: string | null } | null> {
  const snap = await getDocs(
    query(collection(db, 'projects'), where('sourceLeadId', '==', leadId)),
  );
  const d = snap.docs[0];
  if (!d) return null;
  const p = d.data() as Record<string, unknown>;
  return {
    projectId: d.id,
    clientNo: typeof p.clientNo === 'string' ? p.clientNo : '',
    clientId: typeof p.clientId === 'string' && p.clientId ? p.clientId : null,
  };
}

/**
 * THE live convert. Independent counters (spec Q1): the PROJECT number is
 * always minted fresh from `counters/project_av`; the CLIENT number is
 * minted fresh ONLY for a new client, or reused for an existing one.
 * Writes the real `projects/{pid}` doc (carrying `clientId` — the IA-fix
 * source-of-truth link) + creation ledger entry, and — for a new client —
 * the `clients/{cid}` master record. The converted lead is RETURNED so the
 * caller persists it via leadStore.updateLead (one write path, the rules
 * whitelist is respected there).
 *
 * `target` defaults to `{ mode: 'new' }` so existing callers are unchanged.
 *
 * PARTNER SPRINT 2026-05-21 — `partnerId` is OPTIONAL (spec §3: a project
 * may have a partner, or none). `null` / omitted → a direct AV engagement,
 * the prior behaviour, unchanged. A non-null id is written to
 * `project.partnerId` so the Partner Portfolio query finds the project.
 */
export async function convertLeadPersist(
  lead: Lead,
  actorName: string,
  nowISO: string,
  target: ConvertClientTarget = { mode: 'new' },
  partnerId: string | null = null,
  /**
   * The new project's own NAME (2026-05-22). User-settable in the convert
   * UI; blank/omitted → `projectDocFromSeed` writes the `<tier> — <client>`
   * default so the project is never nameless.
   */
  projectName: string = '',
  /**
   * DOUBLE-CONVERSION GUARD OPTIONS (2026-07-03, streamline Phase 1d).
   * The accept-driven conversion (quoteAccept) passes its OWN quote id so
   * the quote being accepted never blocks its own convert. Manual callers
   * omit this — every open sent quote blocks the manual door.
   */
  guard: { excludeQuoteId?: string | null } | null = null,
): Promise<ConvertPersistResult> {
  // ── OPEN-SENT-QUOTE GUARD (no double-conversion path) ──
  // A lead with an OPEN SENT quote must be resolved through the quote:
  // Accept it (acceptance drives conversion + seeds the agreed price from
  // the quote total) or void it (supersede/expire). Converting around a
  // sent quote would let the accepted total and the manually-set price race
  // for the same money gate. Fail-closed: a query error blocks the convert
  // (retry) rather than risking a duplicate conversion.
  const openQuoteId = await findOpenSentQuoteIdForLead(
    lead.id,
    guard?.excludeQuoteId ?? null,
  );
  if (openQuoteId) {
    throw new Error(
      `This lead has an open sent quote (${openQuoteId}). Accept it ` +
        `(accepting converts the lead) or void it (supersede/expire) ` +
        `before converting manually.`,
    );
  }

  // Project number is ALWAYS its own fresh serial (independent of client#).
  const projectSeq = await nextProjectSeq();
  const projectId = projectIdFromSeq(projectSeq);

  // Resolve the client: new → mint a C#####; existing → reuse it.
  let clientNo: string;
  let clientId: string;
  const newClientCreated = target.mode === 'new';
  if (target.mode === 'existing') {
    clientNo = target.clientNo;
    clientId = target.clientId;
  } else {
    const clientSeq = await nextClientSeq();
    clientNo = clientNoFromSeq(clientSeq);
    // The convert path uses the client number as the clients/ doc id.
    clientId = clientNo;
  }

  // PURE bridge (Module 1, Option A) — gates on the SERVICE + AGREED PRICE
  // (or No-charge), NOT a separate "Lock quote" tap. Throws if no service /
  // no money figure (the screen's 6 must-haves are surfaced live before this
  // ever fires). Synthesises the rate-card quote snapshot then delegates to
  // convertLeadToProject so the agreed-price → gateAmounts seed is the SAME
  // single money path (resolveGateAmount spine).
  const res = convertLeadDirect(lead, projectId, clientNo, nowISO);
  const assigneeUid = lead.assigneeUid;
  const assigneeName = lead.assigneeName || lead.ownerName || 'Unassigned';

  // 1) The real project doc (same shape ensureDemoProject writes) — now
  //    carrying the clientId source-of-truth link + the optional partnerId.
  //    On an EXISTING client the `clientName` cache is seeded from the
  //    client's master name (when the picker supplied it), NOT the lead —
  //    so the convert never drifts an existing client's name.
  const projectClientName =
    target.mode === 'existing' && target.clientName
      ? target.clientName
      : undefined;
  const pRef = doc(db, 'projects', projectId);
  await setDoc(pRef, {
    ...projectDocFromSeed(
      res.projectSeed,
      assigneeUid,
      assigneeName,
      clientId,
      partnerId,
      projectClientName,
      projectName,
    ),
    // Vex gate F2 — the queryable lead→project back-ref. Written IN the same
    // create as the project itself, so a conversion run that dies before
    // persisting the lead doc still leaves a findable record
    // (findConvertedProjectForLead) and a retry RESUMES this project instead
    // of minting a second one.
    sourceLeadId: lead.id,
    createdAt: serverTimestamp(),
  });

  // 2) Immutable creation ledger entry (project creation is consequential —
  //    mirrors ensureDemoProject's template_version_lock entry).
  await addDoc(collection(db, 'projects', projectId, 'ledger'), {
    eventType: 'template_version_lock',
    tier: res.projectSeed.tier,
    templateVersion: ENGAGEMENT_TEMPLATE_VERSION,
    summary:
      `Project created from lead ${lead.id} · engagementTemplate locked to ` +
      `${ENGAGEMENT_TEMPLATE_VERSION} (${res.projectSeed.tier}). ` +
      `Value ₹${res.projectSeed.valueINR.toLocaleString('en-IN')}. ` +
      (newClientCreated
        ? `New client ${clientNo} created.`
        : `Attached to existing client ${clientNo}.`) +
      (partnerId ? ` Channel partner ${partnerId}.` : ''),
    actorUid: assigneeUid ?? 'system',
    actorName,
    at: serverTimestamp(),
  });

  // 3) The clients/{cid} master record — created ONLY for a new client.
  //    An existing client's master is left untouched (a project attaching
  //    to it must never overwrite its identity / history).
  if (newClientCreated) {
    await setDoc(doc(db, 'clients', clientNo), {
      clientNo,
      name: lead.name,
      phone: lead.phone || '',
      email: lead.email || '',
      location: lead.location || '',
      source_lead_id: lead.id,
      // Contact spine (AD-039) — seed the reverse Contact→Lead link.
      leadRefs: [lead.id],
      tier: res.projectSeed.tier,
      createdAt: serverTimestamp(),
      createdVia: 'front-door-convert',
    });
  } else {
    // EXISTING client — the master's identity/history is NEVER overwritten.
    // Contact spine (AD-039): append ONLY the reverse leadRefs back-ref.
    await updateDoc(doc(db, 'clients', clientId), {
      leadRefs: arrayUnion(lead.id),
      updatedAt: serverTimestamp(),
    });
  }

  // Contact spine (AD-039) — stamp the converted lead's canonical link so the
  // snapshot (notes_internal) carries it back. `contactId = clientId` (the
  // clients/ doc the project attached to); source = 'converted'.
  const linkedLead: Lead = {
    ...res.lead,
    contactId: clientId,
    contactLinkSource: 'converted',
  };

  return { lead: linkedLead, projectId, clientNo, clientId, newClientCreated };
}

/* ════════════════════ Edit + archive (P1 — Khilendra unblock) ════════════════════
 *
 *  The chat-first rebuild dropped every edit/delete affordance. 479 imported
 *  clients + 595 imported projects (all `needsReview`) cannot be corrected
 *  one-by-one without this. Firestore rules already allow `isTeam()` FULL
 *  update on `projects/` (rule ~716) and `clients/` (~290); `delete:if false`
 *  for every entity → there is NO hard delete. "Archive" is therefore a
 *  rules-allowed `archived:true` update, NOT a delete. No firestore.rules
 *  change (HR-shared file). Additive only.
 *
 *  PURE/IMPURE split kept: `projectPatchBody` / `clientPatchBody` are pure +
 *  unit-tested (they only ever emit a bounded, allowed field set + the
 *  audit stamp); the updateDoc/getDoc wrappers are the thin impure seam.
 */

/** The editable surface of a project (everything Khilendra must correct). */
export interface ProjectEditPatch {
  /** The project's own human-readable name (2026-05-22) — editable. */
  projectName?: string;
  /**
   * The human "VP number" (2026-07-17, VP-editable-label design §3). Editable
   * label — the doc id (`projects/{id}`) stays the opaque, permanent internal
   * key every reference points at, so a relabel touches ONLY this one field on
   * one doc and no cross-references change. Normalised (trim + uppercase) in
   * `projectPatchBody`; uniqueness among live projects is guarded by
   * `validateVpEdit` in the UI BEFORE the write. Relabels are audited via the
   * `project_vp_relabel` ledger entry (see `relabelProjectVp`).
   */
  vp?: string;
  clientName?: string;
  /** raw tier CODE — all 595 imports are placeholder, this is the fix. */
  tierCode?: Tier;
  /** human "tier · displayName" mirror, kept in sync with tierCode. */
  tier?: string;
  valueINR?: number;
  assigneeUid?: string;
  assigneeName?: string;
  activeStageKey?: string;
  /**
   * L1A tabbed-page (2026-06-29) — two explicit-now / auto-later workflow
   * booleans. `ownerReviewed` = the owner signed off the draft (gates report
   * render in the L1A derivation); `closingCallDone` = the post-delivery closing
   * call is complete. Written through the existing project-patch path from the
   * Owner-review / Closing-call stage screens. Designed to flip to automatic
   * later (server trigger sets the same field) — the field name stays stable.
   */
  ownerReviewed?: boolean;
  closingCallDone?: boolean;
}

/**
 * PURE — the exact doc body an updateProject write sends. Only known
 * editable keys are forwarded (an unknown key in the patch is dropped, so
 * the write can never smuggle a field), plus the audit stamp. `updatedAt`
 * is filled with serverTimestamp by the impure caller, not here, so this
 * stays unit-testable.
 */
export function projectPatchBody(
  patch: ProjectEditPatch,
  actorName: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  // The project's own name — trimmed so an all-whitespace edit cannot
  // silently blank the field on disk (a blank name reads as "unnamed" and
  // the UI falls back to the VP placeholder).
  if (typeof patch.projectName === 'string')
    out.projectName = patch.projectName.trim();
  // The human VP number — normalised (trim + uppercase) so `vp700`, `VP700 `
  // and `VP700` all land as one canonical value, matching the `VP###` on-disk
  // shape. Uniqueness is validated (`validateVpEdit`) BEFORE this write; this
  // body only enforces the format discipline (mirrors the projectName trim).
  if (typeof patch.vp === 'string') out.vp = patch.vp.trim().toUpperCase();
  if (typeof patch.clientName === 'string') out.clientName = patch.clientName;
  if (typeof patch.tierCode === 'string') out.tierCode = patch.tierCode;
  if (typeof patch.tier === 'string') out.tier = patch.tier;
  if (typeof patch.valueINR === 'number') out.valueINR = patch.valueINR;
  if (typeof patch.assigneeUid === 'string') out.assigneeUid = patch.assigneeUid;
  if (typeof patch.assigneeName === 'string')
    out.assigneeName = patch.assigneeName;
  if (typeof patch.activeStageKey === 'string')
    out.activeStageKey = patch.activeStageKey;
  // L1A workflow booleans (2026-06-29) — must be whitelisted here or the
  // drop-unknown design silently discards them on write.
  if (typeof patch.ownerReviewed === 'boolean')
    out.ownerReviewed = patch.ownerReviewed;
  if (typeof patch.closingCallDone === 'boolean')
    out.closingCallDone = patch.closingCallDone;
  out.updatedBy = actorName;
  return out;
}

/** Apply a correction to a live `projects/{pid}` doc (rules: isTeam update). */
export async function updateProject(
  pid: string,
  patch: ProjectEditPatch,
  actorName: string,
): Promise<void> {
  await updateDoc(doc(db, 'projects', pid), {
    ...projectPatchBody(patch, actorName),
    updatedAt: serverTimestamp(),
  });
}

/* ════════════════════ VP-number relabel (editable label) ════════════════════
 *
 * VP-editable-label design (2026-07-17) §3 — the human "VP number" is the
 * `vp` FIELD, not the doc id. The doc id (`projects/{id}`) is the opaque,
 * permanent internal key every reference points at (`quotes.projectId`,
 * `payments.projectId`, `messages.projectRef`, subcollections, routes), so a
 * relabel rewrites exactly ONE field on ONE doc and changes NO cross-refs.
 *
 * The doc id gave uniqueness for free; the field does not. `validateVpEdit`
 * (PURE, unit-tested) is the guard the UI runs BEFORE the write, against the
 * already-streamed live project list (no extra Firestore read). A relabel is
 * consequential (invoices, client memory) so it is AUDITED — mirrors
 * `relinkProject`'s ledger write. Pure validator + thin impure writer.
 * ──────────────────────────────────────────────────────────────────────── */

/** The result of validating a candidate VP number against the live list. */
export interface VpValidationResult {
  /** true → safe to write; false → blocked (show `error`). */
  ok: boolean;
  /** the normalised candidate (trim + uppercase) — what would be written. */
  value: string;
  /** human-facing reason when `ok` is false, else null. */
  error: string | null;
}

/**
 * PURE — validate a candidate VP number for `selfPid` against the live project
 * list. Normalises (trim + uppercase), rejects blank, and rejects a collision
 * with ANY OTHER project (doc id ≠ `selfPid`, trashed projects excluded — a
 * trashed project's number is free to reuse). On collision the error names the
 * colliding project so staff can find it. Best-effort by design (a hard
 * guarantee would need a reservation counter — overkill for a hand-edited,
 * human-reviewed, reversible label); the impure edit UI folds the live list it
 * already holds, so there is no extra read.
 */
export function validateVpEdit(
  candidate: string,
  allProjects: LiveProject[],
  selfPid: string,
): VpValidationResult {
  const value = String(candidate ?? '').trim().toUpperCase();
  if (!value) {
    return { ok: false, value, error: 'The VP number cannot be blank.' };
  }
  const clash = allProjects.find(
    (p) =>
      p.pid !== selfPid &&
      !p.trashed &&
      (p.vp ?? '').trim().toUpperCase() === value,
  );
  if (clash) {
    return {
      ok: false,
      value,
      error:
        `${value} is already used by ` +
        `${projectLabel(clash.projectName, clash.vp, clash.tierCode)}. ` +
        `Pick another number.`,
    };
  }
  return { ok: true, value, error: null };
}

/**
 * Relabel a project's human VP number (the `vp` field only — the doc id is
 * untouched, so every reference stays intact). Writes the single-field update
 * through the validated `updateProject` patch path, then an immutable
 * `project_vp_relabel` ledger entry (old → new) so the change is traceable —
 * mirrors `relinkProject`'s audit write. Callers MUST run `validateVpEdit`
 * first; this writer does not re-validate (the uniqueness guard is the UI's,
 * against the live list). `oldVp` is stamped verbatim for the audit trail; the
 * new value is normalised the same way `projectPatchBody` normalises it.
 */
export async function relabelProjectVp(
  pid: string,
  newVp: string,
  oldVp: string,
  actorName: string,
): Promise<void> {
  const normalised = String(newVp ?? '').trim().toUpperCase();
  await updateProject(pid, { vp: normalised }, actorName);
  // Audit trail — a VP-number change flows onto invoices, reports and client
  // memory, so it is recorded in the project ledger (mirrors project_relink).
  await addDoc(collection(db, 'projects', pid, 'ledger'), {
    eventType: 'project_vp_relabel',
    summary: `VP number relabelled ${oldVp} → ${normalised}.`,
    fromVp: oldVp,
    toVp: normalised,
    actorUid: 'system',
    actorName,
    at: serverTimestamp(),
  });
}

/* ════════════════════ L1A Astro-Vastu subject link (AVBP references) ══════
 *
 * OWNER CORRECTION 2026-07-02 — AVBP is the SSOT. A project's astro subject is
 * an AVBP **Group** (`families/{id}`) OR AVBP **Contacts** (`clients/{id}`) —
 * NEVER the astro store. These writers persist that choice on the project doc:
 *   • `setProjectLinkedGroup`    → `linked_group_id`    (clears linked_contact_ids)
 *   • `setProjectLinkedContacts` → `linked_contact_ids` (clears linked_group_id)
 * EITHER/OR is enforced here: setting one field always nulls the other, so the
 * two are mutually exclusive by construction. Forge's Astro-Vastu resolve reads
 * these AVBP references and computes charts from each member's birth details.
 *
 * Written CLIENT-SIDE via the existing `isTeam()` project-update rule — the
 * same path `updateProject` / `relinkProject` use (`linked_group_id` /
 * `linked_contact_ids` are ordinary project fields, NOT gate state, so the
 * `gateStateUnchanged()` guard is untouched). No callable, no firestore.rules
 * change. The old `linkProjectAstroFamily` / `linkProjectAstroPersons`
 * callables (astro store) are no longer called from the linking panels.
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * PURE — the exact field set a Group link/unlink writes (extracted 2026-07-03,
 * owner bug: deleting a Group left projects pointing at it). ONE definition so
 * `setProjectLinkedGroup` and the `deleteFamily` fan-out can never drift:
 *   · `linked_group_id` = the new value (null = unlink);
 *   · either/or — always clears `linked_contact_ids`;
 *   · correction propagation (owner ruling 2026-07-03): always clears the
 *     LEGACY astro-store link fields (`astro_family_id` / `astro_person_ids`).
 *     Otherwise the resolver's back-compat cascade (resolveAstroFamily steps
 *     c/d, which only runs when NO new-style link is set) resurrects the
 *     PREVIOUSLY linked astro family the moment the new-style link is cleared
 *     — the exact stale-roster the owner reported on the Astro-Vastu tab.
 * `updatedAt` is added by the impure caller.
 *
 * VOID-WHEN-NO-SUBJECT (owner bug #5, 2026-07-03 — VP678): each body nulls
 * every OTHER subject field, so the write's resulting subject is knowable
 * PURELY from the argument. When it leaves the project with NO astro subject
 * at all (group null / contacts empty), the write ALSO voids
 * `astro_vastu_confirmation` — a confirmation of a subject that no longer
 * exists is void (the stage chip stayed green + the "Confirmed by" banner
 * survived total link removal otherwise). A REPLACEMENT (a non-null group /
 * non-empty contacts) deliberately does NOT touch the confirmation — the
 * 536784c behaviour stands there: snapshot stays, banner keeps its Re-confirm
 * affordance, no auto-clear.
 */
export function projectLinkedGroupBody(
  groupId: string | null,
): Record<string, unknown> {
  return {
    linked_group_id: groupId,
    linked_contact_ids: null,
    astro_family_id: null,
    astro_person_ids: null,
    // group null ⇒ the project ends the write subject-less ⇒ the confirmation
    // is void. A replacement (non-null) keeps it (536784c: Re-confirm, not clear).
    ...(groupId === null ? { astro_vastu_confirmation: null } : {}),
  };
}

/**
 * PURE — the exact field set a Contacts link/unlink writes (the either/or
 * partner of `projectLinkedGroupBody`; same drift-proofing). Empty list
 * normalises to null (= unlinked); the legacy astro fields are ALWAYS cleared
 * (an explicit correction leaves no legacy shadow — 536784c). Subject-to-empty
 * also voids `astro_vastu_confirmation` (see projectLinkedGroupBody).
 */
export function projectLinkedContactsBody(
  contactIds: string[] | null,
): Record<string, unknown> {
  const ids = contactIds && contactIds.length > 0 ? contactIds : null;
  return {
    linked_contact_ids: ids,
    linked_group_id: null,
    astro_family_id: null,
    astro_person_ids: null,
    // emptied contacts ⇒ subject-less ⇒ confirmation void; a non-empty set
    // (replacement / partial removal) keeps it (536784c).
    ...(ids === null ? { astro_vastu_confirmation: null } : {}),
  };
}

/**
 * Link an AVBP **Group** as the project's astro subject (or `null` to unlink).
 * `groupId` is the `families/{id}` doc id. EITHER/OR — this always clears any
 * linked Contacts. Field set: `projectLinkedGroupBody` (one definition, shared
 * with the deleteFamily fan-out).
 */
export async function setProjectLinkedGroup(
  projectId: string,
  groupId: string | null,
): Promise<void> {
  await updateDoc(doc(db, 'projects', projectId), {
    ...projectLinkedGroupBody(groupId),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Link 1–2 AVBP **Contacts** as the project's astro subject (or `null`/[] to
 * unlink). Each id is a `clients/{id}` doc id (= C##### for a front-door
 * client). EITHER/OR — this always clears any linked Group. Field set:
 * `projectLinkedContactsBody` (one definition, shared with the trashClient
 * fan-out).
 */
export async function setProjectLinkedContacts(
  projectId: string,
  contactIds: string[] | null,
): Promise<void> {
  await updateDoc(doc(db, 'projects', projectId), {
    ...projectLinkedContactsBody(contactIds),
    updatedAt: serverTimestamp(),
  });
}

/* ════════════════════ Project ↔ client / partner RE-LINK ════════════════
 *
 * 2026-05-21 (Anil, item 1) — the data-correction tool. The 595 imported
 * projects can have a project pointed at the WRONG client (or the wrong /
 * no partner). Khilendra (staff) must be able to verify and correct that
 * link. This is distinct from `updateProject` (which corrects tier / value
 * / assignee): re-link rewrites the `clientId` source-of-truth reference
 * and the `partnerId`, keeping the `clientNo` / `clientName` display cache
 * in sync (IA-fix invariant: `clientId` is the truth, the others mirror it).
 *
 * Firestore rules already allow `isTeam()` full update on `projects/` —
 * `clientId` / `partnerId` are ordinary project fields (NOT gate state, so
 * the Phase-1-B `gateStateUnchanged()` guard does not touch them). No rules
 * change. Pure `relinkProjectBody` is unit-tested; the updateDoc wrapper is
 * the thin impure seam.
 * ──────────────────────────────────────────────────────────────────────── */

/** The re-link correction — a new client target, optionally a new partner. */
export interface ProjectRelinkPatch {
  /** clients/{id} doc id — the new source-of-truth client link. Required. */
  clientId: string;
  /** C##### display number for the new client (kept as cache). */
  clientNo: string;
  /** Client display name for the new client (kept as cache). */
  clientName: string;
  /** partners/{id} doc id, or null for "direct AV engagement (no partner)". */
  partnerId: string | null;
}

/**
 * PURE — the exact doc body a `relinkProject` write sends. Rewrites the
 * `clientId` link + its `clientNo` / `clientName` display cache together
 * (they must never diverge) and the nullable `partnerId`. Throws if the
 * `clientId` is blank — a project must always belong to exactly one client
 * (spec §3 / Q4). `updatedAt` is filled by the impure caller.
 */
export function relinkProjectBody(
  patch: ProjectRelinkPatch,
  actorName: string,
): Record<string, unknown> {
  const clientId = String(patch.clientId || '').trim();
  if (!clientId) {
    throw new Error('relinkProjectBody: clientId is required');
  }
  return {
    clientId,
    clientNo: patch.clientNo,
    clientName: patch.clientName,
    // null is a real, intended value here ("direct engagement") — write it.
    partnerId: patch.partnerId ?? null,
    relinkedBy: actorName,
    updatedBy: actorName,
  };
}

/**
 * Re-assign which client (and which partner) a project is linked to. The
 * data-correction tool (item 1) — reachable from the project list, the
 * Client Detail flow and the Partner Detail flow. Writes an immutable
 * ledger entry so the correction is auditable.
 */
export async function relinkProject(
  pid: string,
  patch: ProjectRelinkPatch,
  actorName: string,
): Promise<void> {
  await updateDoc(doc(db, 'projects', pid), {
    ...relinkProjectBody(patch, actorName),
    updatedAt: serverTimestamp(),
  });
  // Audit trail — re-linking a project is consequential (it moves money +
  // history between clients), so it is recorded in the project ledger.
  await addDoc(collection(db, 'projects', pid, 'ledger'), {
    eventType: 'project_relink',
    summary:
      `Project re-linked to client ${patch.clientNo} ` +
      `(${patch.clientName})` +
      (patch.partnerId
        ? ` · channel partner ${patch.partnerId}`
        : ' · no channel partner (direct engagement)') +
      `.`,
    actorUid: 'system',
    actorName,
    at: serverTimestamp(),
  });
}

/**
 * Soft-archive a project. Hard delete is impossible (rules: delete:false,
 * audit trail). `archived:true` is an isTeam-allowed update; the lists
 * filter it out of the default view. Reversible (set archived:false).
 *
 * ARCHIVE-FIX 2026-05-22 — archive is DISTINCT from Trash. Archiving a
 * project keeps it in scope (it still rolls up, it is reachable via the
 * "include archived" toggle); Trash removes it. They are independent
 * flags. Archiving / un-archiving a project NEVER touches its client (the
 * old EditPanel wrongly archived the client as a side-effect — fixed).
 */
export async function archiveProject(
  pid: string,
  actorName: string,
  archived = true,
): Promise<void> {
  await updateDoc(doc(db, 'projects', pid), {
    archived,
    archivedBy: actorName,
    updatedBy: actorName,
    updatedAt: serverTimestamp(),
  });
}

/* ════════════════════ Recoverable Trash — projects ════════════════════
 *
 * 2026-05-22 — owner decision: deleted records go to a recoverable Trash,
 * never hard-deleted from the client side. `trashed:true` is an ordinary
 * project field — `firestore.rules` `projects/` `allow update: if isTeam()
 * && gateStateUnchanged()` already permits it (it is NOT `gateState`), so
 * NO firestore.rules change is needed. Permanent purge is the admin-only
 * `purgeTrashedRecord` Cloud Function (admin SDK bypasses `delete:false`).
 *
 * `projectTrashBody` is PURE + unit-tested (bounded field set + audit
 * stamp); the updateDoc wrapper is the thin impure seam.
 */

/** PURE — the exact doc body a trash / restore write sends for a project. */
export function projectTrashBody(
  trashed: boolean,
  actorName: string,
): Record<string, unknown> {
  return {
    trashed,
    // On trash, stamp who + (the impure caller adds the server timestamp);
    // on restore, clear the audit fields so the record reads clean.
    trashedBy: trashed ? actorName : null,
    updatedBy: actorName,
  };
}

/**
 * Send a project to the recoverable Trash (or restore it). A trashed
 * project vanishes from every normal list / picker / rollup; Restore
 * brings it back intact. Standalone — trashing a project NEVER touches its
 * client (relationship rule: a project trash is fine on its own).
 */
export async function trashProject(
  pid: string,
  actorName: string,
  trashed = true,
): Promise<void> {
  await updateDoc(doc(db, 'projects', pid), {
    ...projectTrashBody(trashed, actorName),
    trashedAt: trashed ? serverTimestamp() : null,
    updatedAt: serverTimestamp(),
  });
}

/* ── client master record (clients/{clientNo}) ── */

export interface LiveClient {
  cid: string;
  clientNo: string;
  name: string;
  phone: string;
  email: string;
  location: string;
  primaryProjectId: string;
  /**
   * ISO timestamp the client master doc was created (read-only presentation).
   * The `clients/{id}` doc is written with `createdAt: serverTimestamp()`; this
   * surfaces it (as an ISO string) for the record-header "added <date>" line.
   * `undefined` for a legacy doc that predates the field — the header simply
   * omits the "added" segment rather than fabricate a date.
   */
  createdAt?: string;
  archived: boolean;
  /**
   * Recoverable Trash (2026-05-22, owner decision). `trashed:true` removes
   * the client from EVERY normal list / picker / count — it is not the
   * same as `archived` (archive keeps a record in scope, just hidden from
   * the default view; trash is a removal that only the Trash view shows).
   * A trashed client doc stays a real `clients/{id}` doc, restorable, until
   * an admin permanently purges it via the `purgeTrashedRecord` CF.
   */
  trashed: boolean;
  /** ISO timestamp the client was sent to Trash. */
  trashedAt: string | null;
  /** Actor who sent the client to Trash (audit). */
  trashedBy: string | null;
  // NEW — Module 2 (AD-008: Contact = Person Data Hub)
  phones?: Array<{ number: string; label?: string; whatsApp?: boolean }>;
  emails?: Array<{ address: string; label?: string }>;
  dob?: string;
  tob?: string;
  pob?: PlaceOfBirth;
  prakriti?: string; // 12-type canonical; validated by isPrakriti()
  /**
   * Per-person Health module (astro-subject step ③) — free-text health context
   * read alongside astrology (never diagnosis/prescription). Portal-side PII;
   * NOT synced to the astro engine (Cleo/Vex privacy surface).
   */
  health?: string;
  address?: { street?: string; city?: string; state?: string; pin?: string; country?: string };
  referredBy?: { contactId: string; name: string };
  /** IDs of FamilyGroup docs this contact belongs to (Module 2 Groups). */
  familyIds?: string[];
  astroChartRef?: string;
  astroChartCached?: boolean;
  /**
   * Directly-assigned contact roles (AD-040 + P1 §6). Controlled values —
   * `professional` / `consultant` / `partner` / `family` / `staff`, plus the
   * legacy coordination tags `architect` / `interior_designer`. Folded into the
   * directory lens set by `lensRolesForContactTags` so a hand-assigned role
   * surfaces the contact in the matching lens (Professionals / Partners / …).
   */
  roleTags?: string[];
  /**
   * Contact spine (AD-039, 2026-06-24) — the reverse Contact→Lead link. The
   * ids of every `leads/{id}` that resolves to this contact (matched at
   * creation, manually linked, or carried in on convert). Gives Contact
   * Detail its enquiry history WITHOUT making `contactId` a queryable flat
   * field on every lead (which would need a rules-whitelist change). The
   * `clients/` update rule is bare `isTeam()`, so appending here is free.
   */
  leadRefs?: string[];
  /**
   * Dedup suppression (P1 Contacts, 2026-07-06) — the `cid`s of OTHER contacts
   * a human has CONFIRMED are genuinely different people despite the dedup
   * engine flagging them (shared phone/email/similar name — e.g. two family
   * members on one handset). Written BIDIRECTIONALLY (both records list each
   * other) so the suppression is symmetric, and read by the Duplicates tab to
   * permanently drop any pair where either side lists the other. Additive +
   * `isTeam()`-writable — no rules change.
   */
  notDuplicateOf?: string[];
}

export interface ClientEditPatch {
  name?: string;
  phone?: string;
  email?: string;
  location?: string;
}

/**
 * PURE — the doc body a createClient write sends. The clientNo is the doc
 * id AND mirrored in the body (matches convertLeadPersist's shape). Pure so
 * a test can assert the bounded field set; createdAt is filled impurely.
 */
export function clientCreateBody(
  clientNo: string,
  patch: ClientEditPatch,
  actorName: string,
): Record<string, unknown> {
  return {
    clientNo,
    name: patch.name ?? '',
    phone: patch.phone ?? '',
    email: patch.email ?? '',
    location: patch.location ?? '',
    createdVia: 'portal-manual-create',
    createdBy: actorName,
  };
}

/** PURE — bounded patch body for a clients/{cid} correction. */
export function clientPatchBody(
  patch: ClientEditPatch,
  actorName: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (typeof patch.name === 'string') out.name = patch.name;
  if (typeof patch.phone === 'string') out.phone = patch.phone;
  if (typeof patch.email === 'string') out.email = patch.email;
  if (typeof patch.location === 'string') out.location = patch.location;
  out.updatedBy = actorName;
  return out;
}

/**
 * PURE — read the recoverable-Trash fields off any record doc. Shared by
 * the client / project / partner mappers so the trash shape is identical
 * everywhere. Defensive — a record written before the Trash feature has
 * none of these fields and reads back as a clean not-trashed record.
 */
/**
 * PURE — best-effort Firestore-value → ISO string. Accepts a Firestore
 * `Timestamp` (has `.toDate()`), a `Date`, or an ISO string; anything else →
 * `undefined`. Used to surface `createdAt` on the record header without forcing
 * a `Timestamp` import into presentation code.
 */
function tsToIso(v: unknown): string | undefined {
  if (!v) return undefined;
  if (typeof v === 'string') return v;
  if (v instanceof Date) return v.toISOString();
  const maybe = v as { toDate?: () => Date };
  if (typeof maybe.toDate === 'function') {
    try {
      return maybe.toDate().toISOString();
    } catch {
      return undefined;
    }
  }
  return undefined;
}

export function trashFieldsFromDoc(d: Record<string, unknown>): {
  trashed: boolean;
  trashedAt: string | null;
  trashedBy: string | null;
} {
  return {
    trashed: d.trashed === true,
    trashedAt: typeof d.trashedAt === 'string' ? d.trashedAt : null,
    trashedBy: typeof d.trashedBy === 'string' ? d.trashedBy : null,
  };
}

/** One-shot read of the real `clients/{clientNo}` master doc. */
export async function getClient(
  clientNo: string,
): Promise<LiveClient | null> {
  const snap = await getDoc(doc(db, 'clients', clientNo));
  if (!snap.exists()) return null;
  return clientFromDoc(snap.id, snap.data());
}

/** Apply a correction to the real `clients/{clientNo}` master (isTeam). */
export async function updateClient(
  clientNo: string,
  patch: ClientEditPatch,
  actorName: string,
): Promise<void> {
  await updateDoc(doc(db, 'clients', clientNo), {
    ...clientPatchBody(patch, actorName),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Contact spine (AD-039, 2026-06-24) — append a lead id to a contact's
 * reverse `leadRefs[]`. Called when a lead is auto-matched at creation,
 * manually linked from the suggestion chip, or carried in on convert.
 * `arrayUnion` is idempotent (re-linking the same lead is a no-op), so this
 * is safe to call more than once. The `clients/` update rule is bare
 * `isTeam()` — no firestore.rules change. `clientId` is the clients/{id}
 * doc id (= clientNo for a front-door-converted client).
 */
export async function appendLeadRef(
  clientId: string,
  leadId: string,
  actor: string,
): Promise<void> {
  await updateDoc(doc(db, 'clients', clientId), {
    leadRefs: arrayUnion(leadId),
    updatedAt: serverTimestamp(),
    updatedBy: actor,
  });
}

/**
 * Dedup suppression (P1 Contacts, 2026-07-06) — mark two Contacts as
 * CONFIRMED-DISTINCT ("not a duplicate — keep both"). Writes each contact's id
 * into the OTHER's `notDuplicateOf[]` (arrayUnion, idempotent) in one batch, so
 * the suppression is symmetric and the Duplicates tab never re-flags the pair.
 * Neither record is otherwise touched. `isTeam()` write rule — no rules change.
 */
export async function markContactsNotDuplicate(
  cidA: string,
  cidB: string,
  actorName: string,
): Promise<void> {
  const batch = writeBatch(db);
  batch.set(
    doc(db, 'clients', cidA),
    { notDuplicateOf: arrayUnion(cidB), updatedAt: serverTimestamp(), updatedBy: actorName },
    { merge: true },
  );
  batch.set(
    doc(db, 'clients', cidB),
    { notDuplicateOf: arrayUnion(cidA), updatedAt: serverTimestamp(), updatedBy: actorName },
    { merge: true },
  );
  await batch.commit();
}

/**
 * Soft-archive the client master (delete impossible — rules:delete:false).
 *
 * ARCHIVE-FIX 2026-05-22 — this is the client's OWN archive control. It is
 * called ONLY from the explicit "Archive client" action on Client Detail.
 * It is NO LONGER fired as a side-effect of archiving a project (the old
 * `EditPanel.doArchive` wrongly flipped the whole client master whenever
 * one of its projects was archived — for a multi-project client that was
 * plainly wrong). `archiveProject` and `archiveClient` are now fully
 * decoupled.
 */
export async function archiveClient(
  clientNo: string,
  actorName: string,
  archived = true,
): Promise<void> {
  await updateDoc(doc(db, 'clients', clientNo), {
    archived,
    archivedBy: actorName,
    updatedBy: actorName,
    updatedAt: serverTimestamp(),
  });
}

/* ════════════════════ Recoverable Trash — clients ════════════════════
 *
 * 2026-05-22 — owner decision. `trashed:true` on a client is an ordinary
 * field (`clients/` `allow update: if isTeam()` already permits it — no
 * rules change). RELATIONSHIP RULE: a client that still owns active
 * (non-trashed) projects must NOT be trashed — that would silently orphan
 * the projects. The caller checks `clientTrashBlock` first and, for a
 * duplicate client, uses Merge instead. Permanent purge = the admin-only
 * `purgeTrashedRecord` CF.
 */

/** PURE — the exact doc body a trash / restore write sends for a client. */
export function clientTrashBody(
  trashed: boolean,
  actorName: string,
): Record<string, unknown> {
  return {
    trashed,
    trashedBy: trashed ? actorName : null,
    updatedBy: actorName,
  };
}

/**
 * PURE — the relationship guard for trashing a client. Returns the count of
 * the client's ACTIVE (non-archived, non-trashed) projects; a non-zero
 * count means the client must NOT be trashed (it would orphan them). The
 * caller folds the already-streamed live-project list — no extra read.
 * Archived projects do NOT block (they travel with the client logically),
 * already-trashed projects do not block (they are themselves removed).
 */
export function activeProjectCountForClient(
  projects: LiveProject[],
  clientId: string,
): number {
  return projects.filter(
    (p) =>
      (p.clientId || p.clientNo) === clientId && !p.archived && !p.trashed,
  ).length;
}

/**
 * PURE — should trashing this client be BLOCKED? Returns a human reason
 * string when the client still has active projects, or `null` when it is
 * safe to trash. The UI surfaces the reason and offers Merge instead.
 */
export function clientTrashBlock(
  projects: LiveProject[],
  clientId: string,
): string | null {
  const n = activeProjectCountForClient(projects, clientId);
  if (n === 0) return null;
  return (
    `This client has ${n} active project${n === 1 ? '' : 's'}. ` +
    `Merge the client into another client, or trash the project` +
    `${n === 1 ? '' : 's'} first — a client is never trashed while it ` +
    `still owns active work.`
  );
}

/**
 * Send a client master to the recoverable Trash (or restore it). The
 * RELATIONSHIP RULE is enforced here too as a server-side-of-the-client
 * safety net: `liveProjects` (the already-streamed list) is checked and
 * the trash is REFUSED with a clear error if the client still owns active
 * projects. Pass `liveProjects: []` only when restoring (`trashed:false`)
 * or when the caller has already guaranteed the check.
 */
export async function trashClient(
  clientNo: string,
  actorName: string,
  trashed: boolean,
  liveProjects: LiveProject[] = [],
): Promise<void> {
  if (trashed) {
    const block = clientTrashBlock(liveProjects, clientNo);
    if (block) throw new Error(block);
  }
  await updateDoc(doc(db, 'clients', clientNo), {
    ...clientTrashBody(trashed, actorName),
    trashedAt: trashed ? serverTimestamp() : null,
    updatedAt: serverTimestamp(),
  });

  // ── LINK FAN-OUT (owner bug 2026-07-03): a trashed Contact must not stay an
  // astro subject anywhere. Any project carrying this Contact in
  // `linked_contact_ids` gets the SAME clear `setProjectLinkedContacts` would
  // write (`projectLinkedContactsBody` — one definition, zero drift): the id is
  // removed, an emptied list normalises to null, and the LEGACY astro fields
  // are cleared so the resolver's back-compat cascade can't resurrect the
  // person on the Astro-Vastu tab. BEST-EFFORT by design: the trash itself has
  // already landed above; a fan-out failure is console.warn'd, never thrown.
  if (trashed) {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'projects'),
          where('linked_contact_ids', 'array-contains', clientNo),
        ),
      );
      if (!snap.empty) {
        const batch = writeBatch(db);
        for (const d of snap.docs) {
          const raw = (d.data() as Record<string, unknown>).linked_contact_ids;
          const remaining = Array.isArray(raw)
            ? (raw as unknown[]).filter(
                (x): x is string =>
                  typeof x === 'string' && x.length > 0 && x !== clientNo,
              )
            : [];
          batch.update(d.ref, {
            ...projectLinkedContactsBody(remaining),
            updatedAt: serverTimestamp(),
          });
        }
        await batch.commit();
      }
    } catch (e) {
      console.warn(
        `[trashClient] linked-contact fan-out failed for ${clientNo} (links may be stale):`,
        (e as Error).message,
      );
    }
  }
}

/**
 * Standalone "New Client" create (P2). Mints the next `C#####` serial from
 * the same rules-permitted `counters/client_av` counter convertLeadPersist
 * uses, writes the real `clients/{clientNo}` master, where the `C#####`
 * serial is BOTH the doc id and the `clientNo` field. Returns the new
 * `C#####` clientNo. (2026-05-22 — new clients continue the owner's live
 * `C#####` series; see `clientNoFromSeq`.)
 */
export async function createClient(
  patch: ClientEditPatch,
  actorName: string,
): Promise<string> {
  const seq = await nextClientSeq();
  const clientNo = clientNoFromSeq(seq);
  await setDoc(doc(db, 'clients', clientNo), {
    ...clientCreateBody(clientNo, patch, actorName),
    createdAt: serverTimestamp(),
  });
  return clientNo;
}

/**
 * AD-040 (2026-06-24) — create a ROLE-TAGGED coordination contact (architect /
 * interior designer) on the contact spine. Mints the next `C#####` (same counter
 * as `createClient`) and writes the Person-Data-Hub shape: name + structured
 * `phones[]` (WhatsApp flagged) + `emails[]` + a `roleTags[]` array. Returns the
 * new `C#####`. Allowed by the bare `isTeam()` create rule on `clients/` — no
 * firestore.rules change. `roleTags`/`phones`/`emails` are additive sub-keys.
 */
export async function createCoordinationContact(input: {
  name: string;
  phones?: Array<{ number: string; label?: string; whatsApp?: boolean }>;
  emails?: Array<{ address: string; label?: string }>;
  roleTag: string;
  actorName: string;
}): Promise<string> {
  const seq = await nextClientSeq();
  const clientNo = clientNoFromSeq(seq);
  const phones = input.phones ?? [];
  const emails = input.emails ?? [];
  await setDoc(doc(db, 'clients', clientNo), {
    clientNo,
    name: input.name || '',
    // Legacy flat fields mirror the primary channel (back-compat readers).
    phone: phones[0]?.number ?? '',
    email: emails[0]?.address ?? '',
    location: '',
    phones,
    emails,
    roleTags: [input.roleTag],
    createdVia: 'intake-coordination',
    createdBy: input.actorName,
    createdAt: serverTimestamp(),
  });
  return clientNo;
}

/**
 * AD-040 — idempotently add a role tag (architect / interior_designer) to an
 * EXISTING contact when an intake drawing-set links to it by exact phone/email.
 * `arrayUnion` makes re-tagging a no-op. Bare `isTeam()` update rule — no rules
 * change.
 */
export async function addContactRoleTag(
  clientId: string,
  roleTag: string,
  actorName: string,
): Promise<void> {
  await updateDoc(doc(db, 'clients', clientId), {
    roleTags: arrayUnion(roleTag),
    updatedAt: serverTimestamp(),
    updatedBy: actorName,
  });
}

/* ════════════════════ Live clients list (master records) ════════════════
 *
 * 2026-05-22 (Anil, Bug 1) — the Clients screen was folded ONLY from the
 * live `projects` stream (`clientRowsFromProjects`). A standalone client
 * created via `createClient` writes a `clients/{id}` master doc but has
 * ZERO projects — so it never appeared in the folded list and could not be
 * searched. The owner's "new client vanishes" bug.
 *
 * `listenClients` streams the real `clients/` collection so a master-only
 * client (0 projects) is visible immediately; `mergeClientRows` folds the
 * project rollup INTO those master rows. The Clients screen now reads BOTH
 * streams. Firestore rules already allow `isTeam()` read on `clients/` — no
 * rules change. `clientFromDoc` / `mergeClientRows` are PURE → unit-tested.
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * PURE — map a `clients/{id}` doc to `LiveClient`. One mapper so the live
 * listener + any one-shot read produce the identical shape. Defensive: a
 * partially-imported master doc (missing fields) still maps cleanly.
 */
export function clientFromDoc(
  id: string,
  d: Record<string, unknown>,
): LiveClient {
  return {
    cid: id,
    clientNo: (d.clientNo as string) || id,
    name: (d.name as string) ?? '',
    phone: (d.phone as string) ?? '',
    email: (d.email as string) ?? '',
    location: (d.location as string) ?? '',
    primaryProjectId: (d.primaryProjectId as string) ?? '',
    createdAt: tsToIso(d.createdAt),
    archived: d.archived === true,
    ...trashFieldsFromDoc(d),
    // Module 2 — Person Data Hub fields (AD-008)
    phones: Array.isArray(d.phones) ? (d.phones as Array<{number: string; label?: string; whatsApp?: boolean}>) : [],
    emails: Array.isArray(d.emails) ? (d.emails as Array<{address: string; label?: string}>) : [],
    dob: typeof d.dob === 'string' ? d.dob : undefined,
    tob: typeof d.tob === 'string' ? d.tob : undefined,
    pob: hydratePob(d.pob),
    prakriti: typeof d.prakriti === 'string' ? d.prakriti : undefined,
    address: d.address && typeof d.address === 'object' ? (d.address as LiveClient['address']) : undefined,
    referredBy: d.referredBy && typeof d.referredBy === 'object' && typeof (d.referredBy as Record<string, unknown>).contactId === 'string'
      ? (d.referredBy as { contactId: string; name: string })
      : undefined,
    familyIds: Array.isArray(d.familyIds) ? (d.familyIds as string[]) : undefined,
    astroChartRef: typeof d.astroChartRef === 'string' ? d.astroChartRef : undefined,
    astroChartCached: d.astroChartCached === true,
    // Directly-assigned contact roles (AD-040 + P1 §6) — string-filtered.
    roleTags: Array.isArray(d.roleTags)
      ? (d.roleTags as unknown[]).filter((x): x is string => typeof x === 'string')
      : undefined,
    // Contact spine (AD-039) — reverse Contact→Lead link.
    leadRefs: Array.isArray(d.leadRefs)
      ? (d.leadRefs as unknown[]).filter((x): x is string => typeof x === 'string')
      : undefined,
    // Dedup suppression (P1 Contacts) — confirmed-distinct contact ids.
    notDuplicateOf: Array.isArray(d.notDuplicateOf)
      ? (d.notDuplicateOf as unknown[]).filter((x): x is string => typeof x === 'string')
      : undefined,
  };
}

/**
 * Live realtime list of every `clients/{id}` master record. Mirrors
 * `listenPartners` — a client master is its OWN doc (the IA fix added the
 * standalone "+ New Client" create path). Sorted newest-by-clientNo.
 *
 * The 2-arg form `listenClients(cb, onErr)` is kept for parity with the
 * other listeners; the preferred form passes a `ListenOpts` object so a
 * caller can also learn when the first snapshot has arrived (`onReady`).
 */
export function listenClients(
  cb: (clients: LiveClient[]) => void,
  optsOrErr?: ListenOpts | ((e: Error) => void),
): Unsubscribe {
  const opts: ListenOpts =
    typeof optsOrErr === 'function' ? { onErr: optsOrErr } : optsOrErr ?? {};
  let readyFired = false;
  const fireReady = () => {
    if (!readyFired) {
      readyFired = true;
      opts.onReady?.();
    }
  };
  return onSnapshot(
    query(collection(db, 'clients')),
    (snap) => {
      let rows: LiveClient[] = snap.docs.map((dd) =>
        clientFromDoc(dd.id, dd.data()),
      );
      // Trash filter — a trashed client is hidden from every normal list.
      if (!opts.includeTrashed) rows = rows.filter((r) => !r.trashed);
      rows.sort((a, b) => (a.clientNo < b.clientNo ? 1 : -1));
      cb(rows);
      fireReady();
    },
    (e) => {
      opts.onErr?.(e);
      fireReady();
    },
  );
}

/**
 * Live realtime list of ONLY the trashed `clients/` master records — the
 * Trash view's clients section. Newest-trashed first.
 */
export function listenTrashedClients(
  cb: (clients: LiveClient[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'clients')),
    (snap) => {
      const rows = snap.docs
        .map((dd) => clientFromDoc(dd.id, dd.data()))
        .filter((r) => r.trashed);
      rows.sort((a, b) =>
        (a.trashedAt || '') < (b.trashedAt || '') ? 1 : -1,
      );
      cb(rows);
    },
    (e) => onErr?.(e),
  );
}

/**
 * PURE — merge the project-folded client rows with the `clients/` master
 * records. Every master doc becomes a row even when it owns ZERO projects
 * (the Bug-1 fix: a standalone client must always be visible / searchable);
 * a master that already has a folded project row keeps the project rollup
 * but takes its name / number from the AUTHORITATIVE master doc. The result
 * is keyed by `clientId` so no client appears twice.
 *
 * `includeArchivedClients` — when false (default) an archived master doc is
 * dropped UNLESS it still owns projects (so a client is never silently
 * lost). `clientFold` rows for a client with no master doc pass through
 * unchanged (a legacy import not yet given a master record).
 */
export function mergeClientRows(
  clientFold: ClientRow[],
  masters: LiveClient[],
  includeArchivedClients = false,
): ClientRow[] {
  const byId = new Map<string, ClientRow>();
  for (const r of clientFold) byId.set(r.clientId, r);
  for (const m of masters) {
    const existing = byId.get(m.cid);
    if (existing) {
      // A master doc exists → it is the source of truth for name + number.
      byId.set(m.cid, {
        ...existing,
        clientNo: m.clientNo || existing.clientNo,
        name: m.name || existing.name,
      });
    } else {
      // Master-only client (0 projects). Drop a soft-archived master only
      // when explicitly excluded — it owns no project so it cannot survive
      // via the fold.
      if (m.archived && !includeArchivedClients) continue;
      byId.set(m.cid, {
        clientId: m.cid,
        clientNo: m.clientNo,
        name: m.name,
        projectCount: 0,
        archivedCount: 0,
        totalValueINR: 0,
      });
    }
  }
  return [...byId.values()].sort((a, b) =>
    a.clientNo < b.clientNo ? 1 : -1,
  );
}

/* ════════════════════ Canonical client-name resolver ════════════════════
 *
 * 2026-05-22 (Anil — name-drift bug) — a project doc carries `clientName`
 * as a DISPLAY CACHE (`rowFromDoc`, `LiveProject.clientName`). That cache
 * can drift from the `clients/{id}` master: e.g. one of a client's
 * projects was imported with a project/site descriptor in `clientName`
 * while the master holds the real client name. AVBP_DATA_MODEL_IA_SPEC_v1
 * is explicit — the client name has ONE canonical source, the
 * `clients/{id}` master. Every screen that shows "the client's name" must
 * read it from that master, not from the per-project cache, so the same
 * client never shows two different names across screens.
 *
 * `clientNameResolver` builds a `clientId → master name` lookup from the
 * live `clients` stream; `resolveClientName` applies it with a safe
 * fallback to the project cache for any project whose client has no
 * master doc yet (a legacy import not given a master record). PURE — the
 * caller streams `listenClients` once and folds it here; no extra read.
 * ──────────────────────────────────────────────────────────────────── */

/** A bound resolver: clientId → canonical client name (master wins). */
export type ClientNameResolver = (
  clientId: string | null | undefined,
  fallbackName?: string | null,
) => string;

/**
 * PURE — build a `ClientNameResolver` from the live `clients` master list.
 * The resolver returns the master's `name` when a master exists for the
 * given `clientId`; otherwise it returns `fallbackName` (the project's
 * cached `clientName`) so a project whose client has no master doc still
 * shows a name. An empty master name never wins over a real fallback.
 */
export function clientNameResolver(
  masters: LiveClient[],
): ClientNameResolver {
  const byId = new Map<string, string>();
  for (const m of masters) {
    if (m.name && m.name.trim()) byId.set(m.cid, m.name);
  }
  return (clientId, fallbackName) => {
    const master = clientId ? byId.get(clientId) : undefined;
    if (master) return master;
    const fb = (fallbackName ?? '').trim();
    return fb || '—';
  };
}

/**
 * PURE — one-shot canonical client name. Convenience wrapper for a caller
 * that has the master list but not a pre-built resolver.
 */
export function resolveClientName(
  clientId: string | null | undefined,
  fallbackName: string | null | undefined,
  masters: LiveClient[],
): string {
  return clientNameResolver(masters)(clientId, fallbackName);
}

/* ════════════════════ PARTNER master record (partners/{partnerNo}) ═══════
 *
 * PARTNER SPRINT 2026-05-21 — AVBP_DATA_MODEL_IA_SPEC_v1 §2 / Q6. A partner
 * is a channel partner who refers / co-brands work; one partner is linked
 * to many projects (`project.partnerId`). This layer is the AV-side admin
 * basics — create / read / edit / archive a partner master + the pure
 * partner-grouped aggregators. It MIRRORS the client master layer above
 * byte-for-byte (same pure/impure split, same bounded-patch discipline,
 * same soft-archive — rules forbid hard delete).
 *
 * NOT in this sprint (Phase 6): partner LOGIN role, the co-branded
 * end-client portal, partner wholesale pricing/billing. `partners/` team
 * create/read/write needs the same rules block `clients/` already has —
 * see the flag in AVBP_STATE.md.
 * ──────────────────────────────────────────────────────────────────── */

/** The partners/{partnerNo} master record — mirrors LiveClient. */
export interface LivePartner {
  /** partners/{id} doc id — the source-of-truth partner key. */
  pid: string;
  partnerNo: string;
  /** Contact-person name (the individual). */
  name: string;
  /**
   * Firm / company name — item 2 (2026-05-21). The partnership is usually
   * with a FIRM; `name` is the contact person, `firmName` is the business.
   * Feeds co-branded report auto-fill later. Optional ('' when unknown).
   */
  firmName: string;
  phone: string;
  email: string;
  /**
   * Partner website — item 2 (2026-05-21). Feeds co-branded report
   * auto-fill later. Stored as entered; optional ('' when unknown).
   */
  website: string;
  location: string;
  /** Free-text notes about the partnership (spec §2 — name, contact, notes). */
  notes: string;
  /** soft-archived (rules forbid hard delete) — hidden from default list. */
  archived: boolean;
  /**
   * Recoverable Trash (2026-05-22, owner decision) — see `LiveClient.trashed`.
   * A trashed partner vanishes from every normal Partners list / picker;
   * the doc survives, restorable, until an admin permanently purges it.
   */
  trashed: boolean;
  trashedAt: string | null;
  trashedBy: string | null;
}

/** The editable surface of a partner — mirrors ClientEditPatch + notes. */
export interface PartnerEditPatch {
  name?: string;
  /** Firm / company name — item 2. */
  firmName?: string;
  phone?: string;
  email?: string;
  /** Partner website — item 2. */
  website?: string;
  location?: string;
  notes?: string;
}

/**
 * PURE — the doc body a createPartner write sends. The partnerNo is the doc
 * id AND mirrored in the body (mirrors clientCreateBody). Pure so a test
 * can assert the bounded field set; createdAt is filled impurely.
 */
export function partnerCreateBody(
  partnerNo: string,
  patch: PartnerEditPatch,
  actorName: string,
): Record<string, unknown> {
  return {
    partnerNo,
    name: patch.name ?? '',
    // Item 2 — firm name + website persisted from create.
    firmName: patch.firmName ?? '',
    phone: patch.phone ?? '',
    email: patch.email ?? '',
    website: patch.website ?? '',
    location: patch.location ?? '',
    notes: patch.notes ?? '',
    createdVia: 'portal-manual-create',
    createdBy: actorName,
  };
}

/** PURE — bounded patch body for a partners/{pid} correction. */
export function partnerPatchBody(
  patch: PartnerEditPatch,
  actorName: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (typeof patch.name === 'string') out.name = patch.name;
  if (typeof patch.firmName === 'string') out.firmName = patch.firmName;
  if (typeof patch.phone === 'string') out.phone = patch.phone;
  if (typeof patch.email === 'string') out.email = patch.email;
  if (typeof patch.website === 'string') out.website = patch.website;
  if (typeof patch.location === 'string') out.location = patch.location;
  if (typeof patch.notes === 'string') out.notes = patch.notes;
  out.updatedBy = actorName;
  return out;
}

/**
 * PURE — map a `partners/{id}` doc to `LivePartner`. One mapper so the
 * one-shot read (`getPartner`) and the live listener (`listenPartners`)
 * surface the same shape — including the item-2 `firmName` / `website`
 * fields, which default to '' for any partner doc written before item 2.
 * Exported for unit tests.
 */
export function partnerFromDoc(
  id: string,
  d: Record<string, unknown>,
): LivePartner {
  return {
    pid: id,
    partnerNo: (d.partnerNo as string) ?? id,
    name: (d.name as string) ?? '',
    firmName: (d.firmName as string) ?? '',
    phone: (d.phone as string) ?? '',
    email: (d.email as string) ?? '',
    website: (d.website as string) ?? '',
    location: (d.location as string) ?? '',
    notes: (d.notes as string) ?? '',
    archived: d.archived === true,
    ...trashFieldsFromDoc(d),
  };
}

/** One-shot read of the real partners/{partnerNo} master doc. */
export async function getPartner(
  partnerNo: string,
): Promise<LivePartner | null> {
  const snap = await getDoc(doc(db, 'partners', partnerNo));
  if (!snap.exists()) return null;
  return partnerFromDoc(snap.id, snap.data());
}

/** Apply a correction to the real partners/{partnerNo} master (isTeam). */
export async function updatePartner(
  partnerNo: string,
  patch: PartnerEditPatch,
  actorName: string,
): Promise<void> {
  await updateDoc(doc(db, 'partners', partnerNo), {
    ...partnerPatchBody(patch, actorName),
    updatedAt: serverTimestamp(),
  });
}

/** Soft-archive the partner master (delete impossible — rules:delete:false). */
export async function archivePartner(
  partnerNo: string,
  actorName: string,
  archived = true,
): Promise<void> {
  await updateDoc(doc(db, 'partners', partnerNo), {
    archived,
    archivedBy: actorName,
    updatedBy: actorName,
    updatedAt: serverTimestamp(),
  });
}

/* ════════════════════ Recoverable Trash — partners ════════════════════
 *
 * 2026-05-22 — owner decision. `trashed:true` on a partner is an ordinary
 * field (`partners/` `allow update: if isTeam()` already permits it — no
 * rules change). A partner is the channel-partner reference on a project
 * (`project.partnerId`); trashing a partner does NOT touch its projects —
 * a partner-less project (`partnerId:null`) is a perfectly valid direct AV
 * engagement, so there is no orphan risk and no relationship block.
 */

/** PURE — the exact doc body a trash / restore write sends for a partner. */
export function partnerTrashBody(
  trashed: boolean,
  actorName: string,
): Record<string, unknown> {
  return {
    trashed,
    trashedBy: trashed ? actorName : null,
    updatedBy: actorName,
  };
}

/** Send a partner master to the recoverable Trash (or restore it). */
export async function trashPartner(
  partnerNo: string,
  actorName: string,
  trashed = true,
): Promise<void> {
  await updateDoc(doc(db, 'partners', partnerNo), {
    ...partnerTrashBody(trashed, actorName),
    trashedAt: trashed ? serverTimestamp() : null,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Standalone "New Partner" create. Mints the AV-P-#### serial from the
 * `counters/partner_av` counter (independent of client/project counters),
 * writes the real `partners/{partnerNo}` master. Returns the new partnerNo.
 * Mirrors `createClient`.
 */
export async function createPartner(
  patch: PartnerEditPatch,
  actorName: string,
): Promise<string> {
  const seq = await nextPartnerSeq();
  const partnerNo = partnerNoFromSeq(seq);
  await setDoc(doc(db, 'partners', partnerNo), {
    ...partnerCreateBody(partnerNo, patch, actorName),
    createdAt: serverTimestamp(),
  });
  return partnerNo;
}

/**
 * Live-listener options shared by `listenProjects` / `listenPartners`.
 *
 * SLOW-LIST FIX 2026-05-21 — `onReady` fires exactly ONCE, on the first
 * snapshot (success OR error). A list screen flips its `loading` flag
 * false in this callback so the empty state ("no clients yet") can never
 * flash DURING a fetch — it shows only when the fetch finished with zero
 * rows. `onErr` still fires on every error.
 */
export interface ListenOpts {
  onErr?: (e: Error) => void;
  onReady?: () => void;
  /**
   * Recoverable Trash (2026-05-22). DEFAULT `false` → a trashed record is
   * filtered OUT of the stream, so it vanishes immediately from every
   * normal list / picker / count / search the moment it is trashed. The
   * Trash view passes `includeTrashed: true` to see them. `archived` is
   * unaffected — that is a separate, independent flag.
   */
  includeTrashed?: boolean;
}

/**
 * Live realtime list of every partner master. Newest by partner number
 * (the serial is monotone, so descending partnerNo≈newest). Mirrors
 * `listenProjects` but reads the `partners/` collection directly (a
 * partner is its own master record, not folded from projects).
 *
 * The 2-arg form `listenPartners(cb, onErr)` is kept for back-compat; the
 * preferred form passes a `ListenOpts` object so a caller can also learn
 * when the first snapshot has arrived (`onReady`).
 */
export function listenPartners(
  cb: (partners: LivePartner[]) => void,
  optsOrErr?: ListenOpts | ((e: Error) => void),
): Unsubscribe {
  const opts: ListenOpts =
    typeof optsOrErr === 'function' ? { onErr: optsOrErr } : optsOrErr ?? {};
  let readyFired = false;
  const fireReady = () => {
    if (!readyFired) {
      readyFired = true;
      opts.onReady?.();
    }
  };
  return onSnapshot(
    query(collection(db, 'partners')),
    (snap) => {
      let rows: LivePartner[] = snap.docs.map((dd) =>
        partnerFromDoc(dd.id, dd.data()),
      );
      // Trash filter — a trashed partner is hidden from every normal list.
      if (!opts.includeTrashed) rows = rows.filter((r) => !r.trashed);
      rows.sort((a, b) => (a.partnerNo < b.partnerNo ? 1 : -1));
      cb(rows);
      fireReady();
    },
    (e) => {
      opts.onErr?.(e);
      fireReady();
    },
  );
}

/**
 * Live realtime list of ONLY the trashed `partners/` records — the Trash
 * view's partners section. Newest-trashed first.
 */
export function listenTrashedPartners(
  cb: (partners: LivePartner[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'partners')),
    (snap) => {
      const rows = snap.docs
        .map((dd) => partnerFromDoc(dd.id, dd.data()))
        .filter((r) => r.trashed);
      rows.sort((a, b) =>
        (a.trashedAt || '') < (b.trashedAt || '') ? 1 : -1,
      );
      cb(rows);
    },
    (e) => onErr?.(e),
  );
}

/* ════════════════════ Live projects list ════════════════════ */

/**
 * The project's own human-readable NAME (2026-05-22, Anil) — distinct from
 * the client name and from the `VP###` code. A client may own several
 * projects of the same tier (e.g. an L1B for their residence AND an L1B for
 * their office); without a per-project name those rows are indistinguishable.
 *
 *  FIELD DECISION — `projectName` is a NEW dedicated field, NOT the existing
 *  `displayName`. On a project doc, `displayName` already holds the TIER's
 *  Modern+Sanskrit name (`projectDocFromSeed` writes `displayName:
 *  seed.displayName` = `tpl.displayName`, e.g. "The Clarity Path · Darshan").
 *  Repurposing it would conflate the project name with the tier label. A
 *  fresh `projectName` field keeps the three identities cleanly separate:
 *  client name (master), tier (`displayName`/`tierCode`), project name.
 *
 *  GRACEFUL FALLBACK — a project doc written before this field exists (every
 *  current live project) has no `projectName`. `rowFromDoc` leaves it `''`;
 *  `projectLabel(...)` derives a `VP### · <tier>` placeholder so nothing
 *  looks broken — the field is simply there to be filled in.
 */

/** Build the human label for a project row — the project name when set,
 *  otherwise a graceful `VP### · <tier>` placeholder. `tierText` is the
 *  short tier code or the rendered "tier · displayName" string. Pure. */
export function projectLabel(
  projectName: string | null | undefined,
  vp: string,
  tierText?: string | null,
): string {
  const name = String(projectName ?? '').trim();
  if (name) return name;
  const tier = String(tierText ?? '').trim();
  return tier ? `${vp} · ${tier}` : vp;
}

/**
 * AGREED-PRICE change-history entry (2026-06-15). One row per fee edit on a
 * live project — what it was, what it became (currency + amount), who changed
 * it and when. Money changes are logged so nothing changes silently (MOCKUP_3).
 */
export interface FeeHistoryEntry {
  /** ISO timestamp of the change (client-stamped at save). */
  at: string;
  /** Who made the change (display name / email). */
  by: string;
  /** Currency BEFORE the change (null = first time a price was set). */
  fromCurrency: 'INR' | 'USD' | null;
  /** Amount BEFORE the change in `fromCurrency` units (null = first set). */
  fromValue: number | null;
  /** Currency AFTER the change. */
  toCurrency: 'INR' | 'USD';
  /** Agreed amount AFTER the change in `toCurrency` units. */
  toValue: number;
  /** Optional short note (e.g. "Negotiated rate"). */
  note?: string;
}

/**
 * Module 4 (AD-013) — a project's reference to a Property entity.
 * Stored in `projects/{pid}.propertyRefs[]`. The bidirectional link is maintained
 * by also writing `properties/{pid}.engagementRefs[]`.
 */
export interface PropertyRef {
  propertyId: string;
  role: 'primary' | 'candidate';
  linkedAt: string;      // ISO timestamp
}

/** The shape Clients/ProjectDetail need — a live project row. */
export interface LiveProject {
  pid: string;
  vp: string;
  /**
   * The project's own name (2026-05-22). Empty string when the project has
   * not been named yet (every pre-field project doc) — callers render
   * `projectLabel(projectName, vp, tier)` for a graceful placeholder.
   */
  projectName: string;
  clientName: string;
  clientNo: string;
  /**
   * IA FIX 2026-05-21 — the SOURCE-OF-TRUTH reference to `clients/{id}`.
   * `clientName`/`clientNo` are display cache; `clientId` is authoritative.
   * Falls back to `clientNo` for any project doc written before the IA fix
   * (the migration backfills it; this fallback keeps a pre-migration row
   * still groupable by client number).
   */
  clientId: string;
  /**
   * IA FIX 2026-05-21 — nullable channel-partner reference. Always present
   * on the model (defined now); the Partner Portfolio UI is the later
   * Partner sprint (spec Q6).
   */
  partnerId: string | null;
  /** raw tier code for engagementTemplate lookups. */
  tierCode: Tier;
  /** human "tier · displayName". */
  tier: string;
  activeStageKey: string;
  assigneeUid: string;
  assigneeName: string;
  valueINR: number;
  clearedGateIds: string[];
  /** soft-archived (rules forbid hard delete) — hidden from default list. */
  archived: boolean;
  /**
   * Recoverable Trash (2026-05-22, owner decision) — see `LiveClient.trashed`.
   * A trashed project vanishes from every normal Projects list / picker /
   * client rollup; the doc survives, restorable, until an admin purges it.
   * Distinct from `archived` (archive keeps the project in scope).
   */
  trashed: boolean;
  trashedAt: string | null;
  trashedBy: string | null;
  /**
   * Per-project payment-mode override (owner directive 2026-05-19). null =
   * no override → the org default applies (resolvePaymentMode). Only the
   * two valid literals are surfaced; any other on-disk value reads as null.
   */
  paymentMode: 'payment_before' | 'work_first' | null;
  /* ── Money-module fields (slice 1 foundation + slice 2 record-payment) ──
   * Read DEFENSIVELY off the project doc. A project written before the
   * money module stamped them has none → INR / auto-firm / no GST flag /
   * empty paid-map, exactly the foundation's stated defaults. */
  /** Billing currency — 'USD' only when stamped, else 'INR' (default). */
  currency: 'INR' | 'USD';
  /** Explicit firm override (admin-set); null → auto-route from currency/gst. */
  firmId: 'firm1' | 'firm2' | null;
  /** GST-billed? null when never stamped (the money model derives a default). */
  gst: boolean | null;
  /**
   * AGREED-PRICE FIX (2026-06-15) — per-gate expected amounts in the
   * engagement's own currency, set from the agreed price on the project (the
   * $40 fix). Empty when the project was never priced beyond the standing INR
   * rate (template wins). The money module prefers this over the template gate
   * `amountINR`. Closes the "$11,000" display bug.
   */
  gateAmounts: Record<string, number>;
  /**
   * AGREED-PRICE change history (2026-06-15) — append-only log of fee edits
   * (was → now, by, when) for the live-project agreed-price control. Empty for
   * projects that never had their fee edited.
   */
  feeHistory: FeeHistoryEntry[];
  /**
   * `gateState.moneyPaid` — the boolean per-gate-id paid map (THE single
   * money authority). Empty object when no gate is cleared yet. Read-only
   * here; only `markPaidManual` (server) ever flips it.
   */
  moneyPaid: Record<string, boolean>;
  /**
   * CLEAN MANUAL PAYMENTS (2026-06-13, owner directive). The Payments screen
   * must NOT auto-aggregate the full 600-project legacy dump (mostly test /
   * "ZZ TEST" / "safe to delete" / unpriced placeholders — the false
   * ₹46,75,000 / 602-project total). Instead the owner adds the REAL
   * engagements he wants tracked one-by-one. `trackedInPayments:true` is the
   * explicit "this engagement is tracked in Payments" flag — only such
   * projects feed the Payments overview / ₹-$ split / firm rollup. Defaults
   * FALSE for every existing project (so Payments starts effectively empty);
   * `stampProjectMoney` sets it true when the owner prices an engagement, and
   * `setProjectTrackedInPayments` toggles it. NO project data is deleted —
   * untracked projects simply do not surface as money.
   */
  trackedInPayments: boolean;
  /**
   * Module 2 (AD-009) — per-project participant matrix. An ordered list of
   * contacts with their role on this project. The primary_client entry MUST
   * stay in sync with `clientId` on every write (backward-compat shortcut).
   * Absent on every project written before Module 2; seeded lazily via
   * `seedParticipantsFromLegacy` on first render.
   */
  participants?: ParticipantEntry[];
  /**
   * Module 4 (AD-013) — bidirectional Property entity link. An optional array
   * of Property references. Absent on all existing projects (≡ no Property
   * linked); the system falls back to the intake doc for floor data.
   * `propertyRefs` absent → legacy path; `propertyRefs` present → Property doc path.
   */
  propertyRefs?: PropertyRef[];
  /**
   * L1A tabbed-page (2026-06-29) — explicit-now / auto-later workflow sign-offs.
   * Read DEFENSIVELY (default false on every pre-field project doc). `ownerReviewed`
   * gates report render in the L1A stage derivation; `closingCallDone` marks the
   * post-delivery closing call complete. Both flip to automatic later via the
   * L1A_STAGE_CONFIG.autoDerive seam with no schema change. Optional on the
   * model (absent ≡ false) so every existing LiveProject literal stays valid.
   */
  ownerReviewed?: boolean;
  closingCallDone?: boolean;
  /**
   * L1A Astro-Vastu family link (2026-07-02) — the astro family whose birth
   * charts power the Astro-Vastu tab. Absent/null on every project until staff
   * link one via the "Linked Family" box. `astro_family_id` is authoritative;
   * `astro_family_label` / `astro_family_member_count` are display caches the
   * `linkProjectAstroFamily` callable writes alongside it (so the box can show
   * the linked family without a re-fetch). Read DEFENSIVELY (default null) so
   * every pre-field project doc stays a valid LiveProject.
   */
  astro_family_id?: string | null;
  astro_family_label?: string | null;
  astro_family_member_count?: number | null;
  /**
   * L1A Astro-Vastu individual-contacts link (2026-07-02, build plan Piece 1
   * step ⑤) — the EITHER/OR partner to `astro_family_id`. When a project's astro
   * subject is 1–2 individual Contacts (rather than a full Group), staff link
   * them via the "Linked Contacts" box. `astro_person_ids` are the derived astro
   * person ids (`` `${clientNo}_primary` ``); `astro_person_labels` is the display
   * cache of their names the `linkProjectAstroPersons` callable writes alongside
   * (so the box shows the linked contacts without a re-fetch). EITHER/OR: setting
   * persons clears `astro_family_id` server-side, and vice-versa. Read
   * DEFENSIVELY (default null) so every pre-field project doc stays valid.
   */
  astro_person_ids?: string[] | null;
  astro_person_labels?: string[] | null;
  /**
   * L1A Astro-Vastu subject link (2026-07-02, OWNER CORRECTION) — the AVBP
   * references the Astro-Vastu tab resolves from. A project's astro subject is
   * an AVBP **Group** OR AVBP **Contacts**, never the astro store:
   *   • `linked_group_id`    = the chosen AVBP Group doc id (`families/{id}`);
   *   • `linked_contact_ids` = chosen AVBP Contact ids — the `clients/{id}` doc
   *     ids (= the C##### clientNo for a front-door client), so Forge's resolve
   *     can `getDoc(clients/{id})` and compute each member's chart.
   * EITHER/OR: setting one clears the other (`setProjectLinkedGroup` /
   * `setProjectLinkedContacts`). These SUPERSEDE the legacy astro-store links
   * (`astro_family_id` / `astro_person_ids`), which the Linked-Group /
   * Linked-Contacts panels no longer write. Read DEFENSIVELY (default null) so
   * every pre-field project doc stays a valid LiveProject.
   */
  linked_group_id?: string | null;
  linked_contact_ids?: string[] | null;
  /** BREAK-1 (linkage audit 2026-07-03) — the lead this project was converted
   *  from (`sourceLeadId` back-ref, stamped at conversion since 2026-07-03).
   *  Lets a project-origin quote carry its originating leadId so it passes
   *  the CURRENT deployed create rule. Defensive: null on pre-back-ref docs. */
  sourceLeadId?: string | null;
}

/**
 * AGREED-PRICE — defensive read of the `feeHistory` array off a project doc.
 * Drops malformed rows; never throws. Newest-LAST on disk (append order).
 */
export function feeHistoryFromDoc(raw: unknown): FeeHistoryEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: FeeHistoryEntry[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const e = r as Record<string, unknown>;
    const toCurrency = e.toCurrency === 'USD' ? 'USD' : e.toCurrency === 'INR' ? 'INR' : null;
    if (!toCurrency) continue;
    if (typeof e.toValue !== 'number' || !Number.isFinite(e.toValue)) continue;
    out.push({
      at: typeof e.at === 'string' ? e.at : '',
      by: typeof e.by === 'string' ? e.by : 'Someone',
      fromCurrency:
        e.fromCurrency === 'USD' ? 'USD' : e.fromCurrency === 'INR' ? 'INR' : null,
      fromValue:
        typeof e.fromValue === 'number' && Number.isFinite(e.fromValue)
          ? e.fromValue
          : null,
      toCurrency,
      toValue: e.toValue,
      ...(typeof e.note === 'string' && e.note ? { note: e.note } : {}),
    });
  }
  return out;
}

/** Exported for unit tests (archived-flag mapping + tierCode gating). */
export function rowFromDoc(
  id: string,
  d: Record<string, unknown>,
): LiveProject | null {
  // Only rows minted by the front door (carry a tierCode) become live
  // rows — the demo VP656 fixture has no tierCode and is left to the
  // existing demo scaffold so the reviewer's demo conversation is intact.
  const tierCode = d.tierCode as Tier | undefined;
  if (!tierCode) return null;
  const clientNo = (d.clientNo as string) ?? '—';
  return {
    pid: id,
    vp: (d.vp as string) ?? id,
    // The project's own name — empty string when never set (graceful
    // fallback is `projectLabel(...)` at every render site).
    projectName:
      typeof d.projectName === 'string' ? d.projectName.trim() : '',
    clientName: (d.clientName as string) ?? '—',
    clientNo,
    // IA FIX — clientId is the truth; fall back to clientNo for a project
    // doc written before the IA fix (the migration backfills the real id).
    clientId: (d.clientId as string) || clientNo,
    partnerId:
      typeof d.partnerId === 'string' && d.partnerId ? d.partnerId : null,
    tierCode,
    tier: (d.tier as string) ?? tierCode,
    activeStageKey: (d.activeStageKey as string) ?? '',
    assigneeUid: (d.assigneeUid as string) ?? 'unassigned',
    assigneeName: (d.assigneeName as string) ?? 'Unassigned',
    valueINR: (d.valueINR as number) ?? 0,
    clearedGateIds: (d.clearedGateIds as string[]) ?? [],
    archived: d.archived === true,
    ...trashFieldsFromDoc(d),
    paymentMode:
      d.paymentMode === 'payment_before' || d.paymentMode === 'work_first'
        ? d.paymentMode
        : null,
    // ── Money-module fields — defensive reads (default INR / auto / none) ──
    currency: d.currency === 'USD' ? 'USD' : 'INR',
    firmId:
      d.firmId === 'firm1' || d.firmId === 'firm2' ? d.firmId : null,
    gst: typeof d.gst === 'boolean' ? d.gst : null,
    // AGREED-PRICE FIX — per-gate amounts set from the agreed price (the $40
    // fix); only finite numbers survive a defensive read.
    gateAmounts:
      d.gateAmounts && typeof d.gateAmounts === 'object'
        ? (Object.fromEntries(
            Object.entries(d.gateAmounts as Record<string, unknown>).filter(
              ([, v]) => typeof v === 'number' && Number.isFinite(v),
            ),
          ) as Record<string, number>)
        : {},
    feeHistory: feeHistoryFromDoc(d.feeHistory),
    moneyPaid: moneyPaidFromGateState(d.gateState),
    // CLEAN MANUAL PAYMENTS — only owner-tracked engagements surface as money.
    // Defaults FALSE for every legacy/test project (Payments starts empty).
    trackedInPayments: d.trackedInPayments === true,
    // Module 2 (AD-009) — participant matrix; absent on pre-Module-2 docs.
    participants: Array.isArray(d.participants) ? (d.participants as ParticipantEntry[]) : undefined,
    // Module 4 (AD-013) — property refs; absent on all pre-Module-4 projects.
    propertyRefs: Array.isArray(d.propertyRefs) ? (d.propertyRefs as PropertyRef[]) : undefined,
    // L1A tabbed-page (2026-06-29) — explicit sign-off booleans, default false.
    ownerReviewed: d.ownerReviewed === true,
    closingCallDone: d.closingCallDone === true,
    // L1A Astro-Vastu family link (2026-07-02) — defensive reads; null on every
    // project until staff link a family via the "Linked Family" box.
    astro_family_id: typeof d.astro_family_id === 'string' && d.astro_family_id ? d.astro_family_id : null,
    astro_family_label: typeof d.astro_family_label === 'string' && d.astro_family_label ? d.astro_family_label : null,
    astro_family_member_count:
      typeof d.astro_family_member_count === 'number' && Number.isFinite(d.astro_family_member_count)
        ? d.astro_family_member_count
        : null,
    // L1A Astro-Vastu individual-contacts link (2026-07-02) — defensive reads;
    // null on every project until staff link contacts via the "Linked Contacts"
    // box. Only string ids / labels survive the read.
    astro_person_ids: Array.isArray(d.astro_person_ids)
      ? (d.astro_person_ids as unknown[]).filter((x): x is string => typeof x === 'string' && x.length > 0)
      : null,
    astro_person_labels: Array.isArray(d.astro_person_labels)
      ? (d.astro_person_labels as unknown[]).filter((x): x is string => typeof x === 'string')
      : null,
    // L1A Astro-Vastu subject link (2026-07-02, OWNER CORRECTION) — AVBP Group /
    // Contact references (supersede the legacy astro_* store links). Defensive
    // reads; null on every project until staff link a Group or Contacts.
    linked_group_id:
      typeof d.linked_group_id === 'string' && d.linked_group_id ? d.linked_group_id : null,
    linked_contact_ids: Array.isArray(d.linked_contact_ids)
      ? (d.linked_contact_ids as unknown[]).filter(
          (x): x is string => typeof x === 'string' && x.length > 0,
        )
      : null,
    // conversion back-ref (defensive; absent on pre-2026-07-03 projects).
    sourceLeadId:
      typeof d.sourceLeadId === 'string' && d.sourceLeadId ? d.sourceLeadId : null,
  };
}

/* ════════════════════ Module 4 — Property linking ════════════════════════
 *
 * Add / remove Property references on a project (bidirectional).
 * The project gets `propertyRefs[]`; the property gets `engagementRefs[]`.
 * Both writes happen client-side (two separate updateDoc calls) — no
 * transaction needed since duplicates are harmless (the UI prevents them).
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * PURE planner for `linkPropertyToProject` (2026-06-28 unification, Gap #2/#3).
 *
 * Given the CURRENT refs on both docs, decide which side(s) to write. `arrayUnion`
 * alone cannot dedupe these refs (each carries a fresh `linkedAt`/`addedAt`
 * timestamp), so we id-key the presence check here: a side already carrying the
 * link is skipped (`null`). This makes re-linking a true no-op and prevents the
 * "register-from-intake" double-engagementRef (Gap #3). The OTHER projects' refs
 * are never read or touched → multi-project reuse (arrayUnion accumulation) intact.
 */
export function planPropertyLinkWrites(
  projRefs: Array<{ propertyId?: string }>,
  engRefs: Array<{ projectId?: string }>,
  projectId: string,
  propertyId: string,
  role: 'primary' | 'candidate',
  now: string,
): {
  projectRef: { propertyId: string; role: 'primary' | 'candidate'; linkedAt: string } | null;
  engagementRef: { projectId: string; role: 'primary' | 'candidate'; label: string; addedAt: string } | null;
} {
  const alreadyOnProject = (Array.isArray(projRefs) ? projRefs : []).some(
    (r) => r && r.propertyId === propertyId,
  );
  const alreadyOnProperty = (Array.isArray(engRefs) ? engRefs : []).some(
    (r) => r && r.projectId === projectId,
  );
  return {
    projectRef: alreadyOnProject ? null : { propertyId, role, linkedAt: now },
    engagementRef: alreadyOnProperty ? null : { projectId, role, label: '', addedAt: now },
  };
}

/**
 * Link a Property to a Project (bidirectional). Writes:
 *   · projects/{pid}.propertyRefs[] += { propertyId, role, linkedAt }
 *   · properties/{propertyId}.engagementRefs[] += { projectId, role, addedAt }
 *
 * IDEMPOTENT: reads BOTH sides first, runs the PURE `planPropertyLinkWrites`
 * planner, and writes ONLY the missing side(s). Re-linking the same property is a
 * no-op; `arrayUnion` preserves OTHER projects' refs → multi-project reuse intact.
 */
export async function linkPropertyToProject(
  projectId: string,
  propertyId: string,
  role: 'primary' | 'candidate',
): Promise<void> {
  const now = new Date().toISOString();

  const [projSnap, propSnap] = await Promise.all([
    getDoc(doc(db, 'projects', projectId)),
    getDoc(doc(db, 'properties', propertyId)),
  ]);

  const projRefs = projSnap.exists() && Array.isArray((projSnap.data() as Record<string, unknown>).propertyRefs)
    ? ((projSnap.data() as Record<string, unknown>).propertyRefs as Array<{ propertyId?: string }>)
    : [];
  const engRefs = propSnap.exists() && Array.isArray((propSnap.data() as Record<string, unknown>).engagementRefs)
    ? ((propSnap.data() as Record<string, unknown>).engagementRefs as Array<{ projectId?: string }>)
    : [];

  const { projectRef, engagementRef } = planPropertyLinkWrites(
    projRefs, engRefs, projectId, propertyId, role, now,
  );

  // 1. Add to project's propertyRefs (only if absent)
  if (projectRef) {
    await updateDoc(doc(db, 'projects', projectId), {
      propertyRefs: arrayUnion(projectRef),
      updatedAt: now,
    });
  }

  // 2. Add to property's engagementRefs (only if absent)
  if (engagementRef) {
    await updateDoc(doc(db, 'properties', propertyId), {
      engagementRefs: arrayUnion(engagementRef),
      updatedAt: now,
    });
  }
}

/**
 * Unlink a Property from a Project (bidirectional). Reads both docs first
 * (to find exact stored shapes for arrayRemove), then removes.
 */
export async function unlinkPropertyFromProject(
  projectId: string,
  propertyId: string,
): Promise<void> {
  // Remove from project's propertyRefs (read-modify-write — shapes may vary)
  const projSnap = await getDoc(doc(db, 'projects', projectId));
  if (projSnap.exists()) {
    const d = projSnap.data() as Record<string, unknown>;
    const refs = Array.isArray(d.propertyRefs) ? d.propertyRefs : [];
    const updated = refs.filter((r: unknown) => {
      return !r || typeof r !== 'object' ||
        (r as Record<string, unknown>).propertyId !== propertyId;
    });
    await updateDoc(doc(db, 'projects', projectId), {
      propertyRefs: updated,
      updatedAt: new Date().toISOString(),
    });
  }

  // Remove from property's engagementRefs (read-modify-write)
  const propSnap = await getDoc(doc(db, 'properties', propertyId));
  if (propSnap.exists()) {
    const d = propSnap.data() as Record<string, unknown>;
    const refs = Array.isArray(d.engagementRefs) ? d.engagementRefs : [];
    const updated = refs.filter((r: unknown) => {
      return !r || typeof r !== 'object' ||
        (r as Record<string, unknown>).projectId !== projectId;
    });
    await updateDoc(doc(db, 'properties', propertyId), {
      engagementRefs: updated,
      updatedAt: new Date().toISOString(),
    });
  }
}

/* ════════════════════ CLEAN MANUAL PAYMENTS — helpers ════════════════════
 *
 * 2026-06-13 (owner directive). The Payments view must surface ONLY the
 * engagements the owner explicitly tracks (`trackedInPayments`). The "Add an
 * engagement to Payments" picker, however, should help him by hiding the
 * obvious junk from the legacy import — the "ZZ TEST" / "safe to delete" /
 * placeholder rows — so the list of addable real projects is short and clean.
 * These PURE helpers drive that filtering; no project is ever deleted.
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * PURE — does a project look like a TEST / placeholder / scrap row from the
 * legacy import (so the "add to Payments" picker can hide it by default)? A
 * project name or client name containing an obvious junk marker ("ZZ", "test",
 * "safe to delete", "delete me", "dummy", "sample", "xxx") is treated as test.
 * Conservative + case-insensitive; a genuine project is never matched (the
 * markers are not words that appear in real Vastu engagement names). This is a
 * DISPLAY filter only — the picker offers a "show all" escape so nothing is
 * permanently hidden.
 */
export function isLikelyTestProject(p: {
  projectName?: string;
  clientName?: string;
  vp?: string;
}): boolean {
  const hay = `${p.projectName ?? ''}  ${p.clientName ?? ''}`.toLowerCase();
  // Word/segment markers — `zz` only as a leading token ("ZZ TEST") so a real
  // name that merely contains the letters "zz" mid-word is not caught.
  if (/(^|[\s_-])zz([\s_-]|test|$)/.test(hay)) return true;
  return [
    'safe to delete',
    'delete me',
    'to delete',
    'test ',
    ' test',
    'testing',
    'dummy',
    'sample',
    'placeholder',
    'xxx',
    'do not use',
  ].some((m) => hay.includes(m));
}

/**
 * PURE — read the `gateState.moneyPaid` boolean map off a project doc
 * defensively. The map is THE single money authority (server-flipped only).
 * A doc with no gateState, or a malformed one, reads back as an empty map.
 * Exported for unit tests.
 */
export function moneyPaidFromGateState(
  gateState: unknown,
): Record<string, boolean> {
  if (!gateState || typeof gateState !== 'object') return {};
  const mp = (gateState as Record<string, unknown>).moneyPaid;
  if (!mp || typeof mp !== 'object') return {};
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(mp as Record<string, unknown>)) {
    if (v === true) out[k] = true;
  }
  return out;
}

/**
 * THE CANONICAL "which gates are cleared?" SOURCE (2026-06-16 — Bug A fix).
 *
 * ── THE BUG ──
 * A recorded payment (`markPaidManual`, server) flips ONLY
 * `gateState.moneyPaid[gateId]` — it never touches the legacy
 * `clearedGateIds[]` array field. But the money rollup (`moneyModel` →
 * received/outstanding, the BY FIRM card, the engagement header) and the
 * project-page gate readout were reading `clearedGateIds`, so a $40 advance
 * the owner recorded showed "Paid · $40 collected" in the Record-payment
 * modal (which reads `moneyPaid`) yet the rollup still said "Received $0 ·
 * Outstanding $40" (reading the empty `clearedGateIds`). TWO sources for one
 * fact — the same split-source class as the gate-amount `$11,000` bug.
 *
 * ── THE FIX ──
 * `moneyPaid` is THE authority. A gate is cleared when it is true in
 * `moneyPaid` OR present in the legacy `clearedGateIds` (kept for any project
 * cleared the old way / future legacy writers). Every money surface derives
 * "cleared" through THIS one function, so received/outstanding everywhere is
 * computed from the same source the Record-payment modal already trusted.
 *
 * PURE — exported for unit tests + reused by `moneyModel.moneyProjectFromLive`
 * and the project-page `liveToRow`.
 */
export function effectiveClearedGateIds(p: {
  clearedGateIds?: string[] | null;
  moneyPaid?: Record<string, boolean> | null;
}): string[] {
  const set = new Set<string>();
  for (const id of p.clearedGateIds ?? []) {
    if (typeof id === 'string' && id) set.add(id);
  }
  const mp = p.moneyPaid ?? {};
  for (const [id, paid] of Object.entries(mp)) {
    if (paid === true && id) set.add(id);
  }
  return [...set];
}

/**
 * Live realtime list of every front-door-created project. Newest by
 * client number (the serial is monotone, so descending pid≈newest).
 *
 * The 2-arg form `listenProjects(cb, onErr)` is kept for back-compat; the
 * preferred form passes a `ListenOpts` object so a caller can also learn
 * when the first snapshot has arrived (`onReady`) — used by the list
 * screens to distinguish "still loading" from "genuinely empty" (item 9).
 */
export function listenProjects(
  cb: (projects: LiveProject[]) => void,
  optsOrErr?: ListenOpts | ((e: Error) => void),
): Unsubscribe {
  const opts: ListenOpts =
    typeof optsOrErr === 'function' ? { onErr: optsOrErr } : optsOrErr ?? {};
  let readyFired = false;
  const fireReady = () => {
    if (!readyFired) {
      readyFired = true;
      opts.onReady?.();
    }
  };
  return onSnapshot(
    query(collection(db, 'projects')),
    (snap) => {
      const rows: LiveProject[] = [];
      for (const dd of snap.docs) {
        const r = rowFromDoc(dd.id, dd.data());
        if (!r) continue;
        // Trash filter — a trashed project is hidden from every normal
        // list, picker, count and the client / partner rollups (which all
        // fold this same stream). The Trash view passes includeTrashed.
        if (r.trashed && !opts.includeTrashed) continue;
        rows.push(r);
      }
      // Sort by VP number (numeric) descending — newest project first (owner
      // request 2026-06-17). VP### strings are extracted to integers so
      // VP675 > VP650 > VP1. A malformed/missing vp falls back to 0.
      rows.sort((a, b) => {
        const an = parseInt((a.vp || '').replace(/^VP/i, ''), 10) || 0;
        const bn = parseInt((b.vp || '').replace(/^VP/i, ''), 10) || 0;
        return bn - an;
      });
      cb(rows);
      fireReady();
    },
    (e) => {
      opts.onErr?.(e);
      fireReady();
    },
  );
}

/**
 * Live realtime list of ONLY the trashed `projects/` records — the Trash
 * view's projects section. Newest-trashed first.
 */
export function listenTrashedProjects(
  cb: (projects: LiveProject[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'projects')),
    (snap) => {
      const rows: LiveProject[] = [];
      for (const dd of snap.docs) {
        const r = rowFromDoc(dd.id, dd.data());
        if (r && r.trashed) rows.push(r);
      }
      rows.sort((a, b) =>
        (a.trashedAt || '') < (b.trashedAt || '') ? 1 : -1,
      );
      cb(rows);
    },
    (e) => onErr?.(e),
  );
}

/* ════════════════════ IA FIX — client-grouped views ════════════════════
 *
 * AVBP_DATA_MODEL_IA_SPEC_v1 §5: the "Clients" screen is one row PER
 * CLIENT (not per project). A client's projects = every project whose
 * `clientId` matches. These pure aggregators turn the flat live-project
 * list into client rows + a per-client project list — no extra Firestore
 * read (they fold the SAME `listenProjects` stream the All-Projects screen
 * already subscribes to). Pure → unit-tested directly.
 * ──────────────────────────────────────────────────────────────────── */

/** One row on the IA-fixed Clients list — a client + a project rollup. */
export interface ClientRow {
  /** clients/{id} doc id — the source-of-truth client key. */
  clientId: string;
  /** C##### display number (cache from the projects). */
  clientNo: string;
  /** Client display name (cache from the most-recent project). */
  name: string;
  /** How many of this client's projects (excludes archived). */
  projectCount: number;
  /** How many are archived (informational). */
  archivedCount: number;
  /** Total engagement value summed across the client's live projects. */
  totalValueINR: number;
}

/**
 * PURE — the client-vs-Contact DERIVED status (datamap #1c, Build I).
 *
 * A Contact becomes a **Client** the moment it is linked to a project — as the
 * primary/co client in `participants[]`, OR (legacy projects with no
 * `participants[]`) as the project's `clientId`/`clientNo`. Otherwise it is a
 * general **Contact**. There is NO stored flag and NO separate collection: this
 * single derivation is what the directory's Clients tab AND the record header
 * both call, so the two surfaces can never disagree (it replaces the inline
 * copies that had drifted between `matchesTab` and the row renderer).
 */
export function contactStatus(
  clientId: string,
  projects: LiveProject[],
): 'client' | 'contact' {
  for (const p of projects) {
    if (p.participants && p.participants.length > 0) {
      for (const e of p.participants) {
        if (
          e.contactId === clientId &&
          (e.role === 'primary_client' || e.role === 'co_client')
        ) {
          return 'client';
        }
      }
    } else if ((p.clientId || p.clientNo) === clientId) {
      return 'client';
    }
  }
  return 'contact';
}

/**
 * PURE — fold a flat live-project list into one row per client, keyed by
 * `clientId`. `includeArchived` controls whether archived projects count
 * toward `projectCount` / `totalValueINR` (they are always counted into
 * `archivedCount`). A client with ONLY archived projects still appears
 * (so it is never lost from the list) but with `projectCount: 0`.
 */
export function clientRowsFromProjects(
  projects: LiveProject[],
  includeArchived = false,
): ClientRow[] {
  const byClient = new Map<string, ClientRow>();
  for (const p of projects) {
    const key = p.clientId || p.clientNo;
    const row =
      byClient.get(key) ??
      ({
        clientId: key,
        clientNo: p.clientNo,
        name: p.clientName,
        projectCount: 0,
        archivedCount: 0,
        totalValueINR: 0,
      } satisfies ClientRow);
    // Most-recent non-placeholder name/number wins (list is clientNo-desc).
    if (p.clientName && p.clientName !== '—') row.name = p.clientName;
    if (p.clientNo && p.clientNo !== '—') row.clientNo = p.clientNo;
    if (p.archived) {
      row.archivedCount += 1;
      if (includeArchived) {
        row.projectCount += 1;
        row.totalValueINR += p.valueINR;
      }
    } else {
      row.projectCount += 1;
      row.totalValueINR += p.valueINR;
    }
    byClient.set(key, row);
  }
  return [...byClient.values()].sort((a, b) =>
    a.clientNo < b.clientNo ? 1 : -1,
  );
}

/** PURE — every project belonging to one client (by clientId). */
export function projectsForClient(
  projects: LiveProject[],
  clientId: string,
): LiveProject[] {
  return projects.filter((p) => (p.clientId || p.clientNo) === clientId);
}

/**
 * PURE — case-insensitive client search over the ALREADY-LOADED client
 * rows (spec §5: search by name / number / phone / email). Empty query
 * matches all.
 *
 * Phone search: digit-only query fragment (≥3 digits) is matched against
 * the stripped digits of all phone numbers for this contact.
 * Email search: substring match against all email addresses.
 */
export function matchesClientRow(row: ClientRow, query: string): boolean {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;

  // Digit-only query fragment → also match against stripped phone digits
  const qDigits = q.replace(/\D/g, '');
  if (qDigits.length >= 3) {
    const allPhones = [
      (row as { phone?: string }).phone ?? '',
      ...((row as { phones?: Array<{ number: string }> }).phones ?? []).map(
        (p) => p.number,
      ),
    ]
      .map((n) => n.replace(/\D/g, ''))
      .join(' ');
    if (allPhones.includes(qDigits)) return true;
  }

  const allEmails = [
    (row as { email?: string }).email ?? '',
    ...((row as { emails?: Array<{ address: string }> }).emails ?? []).map(
      (e) => e.address,
    ),
  ].join(' ');

  return `${row.name}  ${row.clientNo}  ${row.clientId}  ${allEmails}`
    .toLowerCase()
    .includes(q);
}

/* ════════════ Lead ↔ existing-client match (hidden-leads fix §3) ════════════
 *
 * 2026-05-22 — when a surfaced lead's phone (or email) matches an existing
 * `clients/{id}` master, the Lead Inbox flags it so staff see at a glance
 * that the "new" lead is in fact an existing client (e.g. "P Krishnan" is
 * both a lead AND client C00233). Pure + unit-tested; the React row only
 * renders the result. Phone match is digit-normalised so `+91 98735 32369`
 * and `9873532369` match; email match is case-insensitive exact.
 */

/** Last-9-digit normalisation — strips country code / spaces / punctuation
 *  so two renderings of the same Indian mobile compare equal. */
export function phoneKey(raw: unknown): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  return digits.length > 9 ? digits.slice(-9) : digits;
}

export interface ClientMatch {
  cid: string;
  clientNo: string;
  name: string;
  /** What matched — for the chip tooltip / display. */
  via: 'phone' | 'email';
}

/**
 * PURE — find an existing `clients/` master that matches a lead's phone or
 * email. Phone is matched first (more reliable for WhatsApp / form leads),
 * then email. Returns `null` when nothing matches or the lead has neither
 * a usable phone nor email. `masters` is the already-streamed `LiveClient`
 * list — no extra Firestore read.
 */
export function matchLeadToClient(
  lead: { phone?: string; email?: string },
  masters: LiveClient[],
): ClientMatch | null {
  const pk = phoneKey(lead.phone);
  if (pk.length >= 7) {
    const m = masters.find((c) => phoneKey(c.phone) === pk);
    if (m) {
      return { cid: m.cid, clientNo: m.clientNo, name: m.name, via: 'phone' };
    }
  }
  const em = String(lead.email ?? '').trim().toLowerCase();
  if (em && em.includes('@')) {
    const m = masters.find(
      (c) => String(c.email ?? '').trim().toLowerCase() === em,
    );
    if (m) {
      return { cid: m.cid, clientNo: m.clientNo, name: m.name, via: 'email' };
    }
  }
  return null;
}

/* ════════════════════ PARTNER SPRINT — partner-grouped views ════════════
 *
 * AVBP_DATA_MODEL_IA_SPEC_v1 §2/§5/Q6: a Partner Portfolio = a partner +
 * the list of their projects + a billing rollup. A project's partner link
 * is `project.partnerId` (added in the IA fix, build 1). These pure
 * aggregators MIRROR the client-grouped views above — but, unlike clients
 * (which exist only as folded project rows on the list), a partner has its
 * OWN master record (`partners/{id}`), so the Partners list is built from
 * `listenPartners` and these helpers attach the project rollup. Pure →
 * unit-tested directly.
 * ──────────────────────────────────────────────────────────────────── */

/** A partner project-rollup — what the Partners list shows per partner. */
export interface PartnerProjectRollup {
  /** How many of this partner's projects (excludes archived). */
  projectCount: number;
  /** How many are archived (informational). */
  archivedCount: number;
  /** Total engagement value summed across the partner's live projects. */
  totalValueINR: number;
}

/**
 * PURE — every project linked to one partner (by `partnerId`). Mirrors
 * `projectsForClient`. A project with no partner (`partnerId === null`) is
 * NEVER matched — even when the lookup id itself is falsy, so a missing /
 * empty partner id can never accidentally return every direct engagement.
 */
export function projectsForPartner(
  projects: LiveProject[],
  partnerId: string | null | undefined,
): LiveProject[] {
  if (!partnerId) return [];
  return projects.filter((p) => p.partnerId === partnerId);
}

/**
 * PURE — the project rollup (count + value) for one partner. Mirrors the
 * count/value fold inside `clientRowsFromProjects`. `includeArchived`
 * controls whether archived projects count toward `projectCount` /
 * `totalValueINR` (always counted into `archivedCount`).
 */
export function partnerProjectRollup(
  projects: LiveProject[],
  partnerId: string,
  includeArchived = false,
): PartnerProjectRollup {
  const out: PartnerProjectRollup = {
    projectCount: 0,
    archivedCount: 0,
    totalValueINR: 0,
  };
  for (const p of projectsForPartner(projects, partnerId)) {
    if (p.archived) {
      out.archivedCount += 1;
      if (includeArchived) {
        out.projectCount += 1;
        out.totalValueINR += p.valueINR;
      }
    } else {
      out.projectCount += 1;
      out.totalValueINR += p.valueINR;
    }
  }
  return out;
}

/**
 * PURE — case-insensitive partner search over the ALREADY-LOADED partner
 * masters. Mirrors `matchesClientRow`; searches name / number / phone /
 * location (a partner master carries all of these, unlike the folded
 * client row). Empty query matches all.
 */
export function matchesPartner(partner: LivePartner, query: string): boolean {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return `${partner.name}  ${partner.firmName}  ${partner.partnerNo}  ${partner.phone}  ${partner.location}`
    .toLowerCase()
    .includes(q);
}

/* ════════════════════ PARTNER SPRINT — new project for a partner ════════
 *
 * Spec §2/§5: a project may carry an OPTIONAL partner. `createProjectForClient`
 * (below) gains an optional `partnerId` so the "+ New Project" flow can
 * attach a partner. A direct AV engagement passes `null` — unchanged.
 * ──────────────────────────────────────────────────────────────────── */

/* ════════════════════ IA FIX — new project for an existing client ═══════
 *
 * Spec §5 / §8(3): "+ New Project for this client" on the Client Detail
 * screen. A new VP### is minted (independent counter); the project is
 * attached to the EXISTING clientId — no new client, no new C#####. This
 * is the third project-creation path (the other two are lead-conversion →
 * new client / → existing client).
 * ──────────────────────────────────────────────────────────────────── */

/** PURE — the project seed for a brand-new project on an existing client.
 *  Pricing is the LOCKED `autoQuote` (one pricing path — never re-derived).
 *  `projectId` is supplied by the impure caller (real counter / test seq). */
export function newProjectSeed(
  projectId: string,
  client: { clientNo: string; name: string },
  tier: CoreTier,
  sqft: number | null,
): ConvertResult['projectSeed'] {
  const tpl = getEngagementTemplate(tier);
  if (!tpl) {
    throw new Error(`newProjectSeed: unknown tier "${tier}"`);
  }
  const quote = autoQuote(tier, sqft);
  const firstMoneyGate = tpl.gates.find((g) => g.kind === 'money');
  return {
    vp: projectId,
    clientName: client.name,
    clientNo: client.clientNo,
    tier,
    displayName: tpl.displayName,
    valueINR: quote.valueINR,
    initialStageKey: tpl.stages[0]?.key ?? '',
    firstMoneyGateId:
      firstMoneyGate && 'id' in firstMoneyGate ? firstMoneyGate.id : null,
    // A brand-new project for an existing client carries no agreed price yet
    // (standing rate) → INR, GST-default, firm auto-route, no gate override.
    // The agreed price is set later from the Set-price screen / Payment tab.
    currency: 'INR',
    gst: true,
    firmId: 'firm1',
    gateAmounts: {},
  };
}

export interface NewProjectResult {
  projectId: string;
  clientId: string;
  clientNo: string;
}

/**
 * Create a new project for an EXISTING client. Mints a fresh VP### from
 * the independent project counter, writes the real `projects/{pid}` doc
 * (carrying the existing `clientId`) + the creation ledger entry. The
 * client master is NOT touched. Pricing is the locked `autoQuote`.
 *
 * PARTNER SPRINT 2026-05-21 — `partnerId` is OPTIONAL (spec §3: a project
 * may have a partner, or none). `null` / omitted → a direct AV engagement,
 * unchanged. A non-null id is written to `project.partnerId` so the
 * Partner Portfolio query (`projectsForPartner`) finds it.
 */
export async function createProjectForClient(
  client: { clientId: string; clientNo: string; name: string },
  tier: CoreTier,
  sqft: number | null,
  actorName: string,
  partnerId: string | null = null,
  /**
   * The new project's own NAME (2026-05-22). User-settable in the "+ New
   * Project" form; blank/omitted → the `<tier> — <client>` default.
   */
  projectName: string = '',
): Promise<NewProjectResult> {
  const projectSeq = await nextProjectSeq();
  const projectId = projectIdFromSeq(projectSeq);
  const seed = newProjectSeed(projectId, client, tier, sqft);

  const pRef = doc(db, 'projects', projectId);
  await setDoc(pRef, {
    ...projectDocFromSeed(
      seed,
      null,
      'Unassigned',
      client.clientId,
      partnerId,
      undefined,
      projectName,
    ),
    createdAt: serverTimestamp(),
  });

  await addDoc(collection(db, 'projects', projectId, 'ledger'), {
    eventType: 'template_version_lock',
    tier: seed.tier,
    templateVersion: ENGAGEMENT_TEMPLATE_VERSION,
    summary:
      `Project created for existing client ${client.clientNo} · ` +
      `engagementTemplate locked to ${ENGAGEMENT_TEMPLATE_VERSION} ` +
      `(${seed.tier}). Value ₹${seed.valueINR.toLocaleString('en-IN')}.` +
      (partnerId ? ` Channel partner ${partnerId}.` : ''),
    actorUid: 'system',
    actorName,
    at: serverTimestamp(),
  });

  return { projectId, clientId: client.clientId, clientNo: client.clientNo };
}

/* ════════════════════ Merge duplicate clients ════════════════════
 *
 * 2026-05-22 — owner decision (Recoverable Trash batch, item D). The
 * imported client master has duplicates: the same real client exists as
 * two `clients/{id}` docs, often with projects split across both. A
 * duplicate that owns projects CANNOT simply be trashed (the relationship
 * rule blocks it). Merge is the correct path:
 *
 *   1. re-point EVERY project of the duplicate onto the SURVIVOR
 *      (`clientId` + `clientNo` rewritten via `relinkProject`, which also
 *      writes a project-ledger audit entry per project);
 *   2. carry over to the survivor any contact field the survivor is
 *      MISSING but the duplicate has (phone / email / location) — the
 *      survivor's own non-blank fields always win;
 *   3. send the now-empty duplicate client to the recoverable Trash.
 *
 * After a merge the duplicate owns 0 projects, so the Trash step passes
 * the relationship guard cleanly. The whole thing is auditable: every
 * re-link writes a ledger entry, and the duplicate is trashed (recoverable)
 * not deleted. Pure helpers (`mergeContactCarryOver`) are unit-tested; the
 * Firestore writes are the impure seam.
 */

/**
 * PURE — compute the contact patch to apply to the SURVIVOR: only the
 * fields the survivor is missing (blank) AND the duplicate has. The
 * survivor's existing non-blank values are never overwritten. Returns an
 * empty object when the survivor needs nothing.
 */
export function mergeContactCarryOver(
  survivor: { phone?: string; email?: string; location?: string },
  duplicate: { phone?: string; email?: string; location?: string },
): ClientEditPatch {
  const out: ClientEditPatch = {};
  const blank = (v: string | undefined) => !v || !v.trim();
  if (blank(survivor.phone) && !blank(duplicate.phone)) {
    out.phone = duplicate.phone!.trim();
  }
  if (blank(survivor.email) && !blank(duplicate.email)) {
    out.email = duplicate.email!.trim();
  }
  if (blank(survivor.location) && !blank(duplicate.location)) {
    out.location = duplicate.location!.trim();
  }
  return out;
}

export interface MergeClientsResult {
  /** How many projects were re-pointed onto the survivor. */
  projectsMoved: number;
  /** true → the survivor's contact record gained carried-over fields. */
  contactCarried: boolean;
  /** The clientId of the (now-trashed) duplicate. */
  trashedClientId: string;
}

/**
 * Merge a DUPLICATE client into a SURVIVOR client. Re-points every project,
 * carries over missing contact fields, trashes the duplicate.
 *
 * `liveProjects` is the already-streamed `LiveProject[]` (no extra read) —
 * every project whose `clientId` (or fallback `clientNo`) is the duplicate
 * is re-linked. `survivor` / `duplicate` are the two client masters.
 */
export async function mergeClients(
  survivor: LiveClient,
  duplicate: LiveClient,
  actorName: string,
  liveProjects: LiveProject[],
): Promise<MergeClientsResult> {
  if (survivor.cid === duplicate.cid) {
    throw new Error('Cannot merge a client into itself.');
  }
  // 1) Re-point every project of the duplicate onto the survivor. Each
  //    relinkProject write rewrites clientId + clientNo + clientName and
  //    appends an immutable project-ledger entry — fully audited.
  const dupProjects = liveProjects.filter(
    (p) => (p.clientId || p.clientNo) === duplicate.cid,
  );
  for (const p of dupProjects) {
    await relinkProject(
      p.pid,
      {
        clientId: survivor.cid,
        clientNo: survivor.clientNo,
        clientName: survivor.name,
        // Preserve the project's existing partner link — merge moves the
        // CLIENT, never the channel partner.
        partnerId: p.partnerId ?? null,
      },
      actorName,
    );
  }

  // 2) Carry over any contact field the survivor is missing.
  const carry = mergeContactCarryOver(survivor, duplicate);
  const contactCarried = Object.keys(carry).length > 0;
  if (contactCarried) {
    await updateClient(survivor.clientNo, carry, actorName);
  }

  // 2b) Contact spine (AD-039) — carry the reverse Contact→Lead links onto the
  //     survivor, and re-point any lead whose `contactId` was the duplicate.
  //     `arrayUnion` is idempotent; the lead re-point goes through the SAME
  //     rules-whitelisted `updateLead` snapshot path (no rules change).
  const dupLeadRefs = duplicate.leadRefs ?? [];
  if (dupLeadRefs.length) {
    try {
      await updateDoc(doc(db, 'clients', survivor.cid), {
        leadRefs: arrayUnion(...dupLeadRefs),
        updatedAt: serverTimestamp(),
      });
    } catch {
      /* non-fatal — the reverse link is convenience, not correctness */
    }
    for (const leadId of dupLeadRefs) {
      try {
        const ld = await getLead(leadId);
        if (ld && ld.contactId === duplicate.cid) {
          await updateLead(
            { ...ld, contactId: survivor.cid, updatedAt: new Date().toISOString() },
            actorName,
            `Contact re-pointed to ${survivor.clientNo} (client merge)`,
          );
        }
      } catch {
        /* one lead's re-point failing must not abort the whole merge */
      }
    }
  }

  // 3) Trash the now-empty duplicate. dupProjects have all been re-pointed,
  //    so the relationship guard passes — pass an empty project list (the
  //    duplicate genuinely owns nothing now).
  await trashClient(duplicate.clientNo, actorName, true, []);

  // 4) Best-effort note on the survivor's append-only notes subcollection
  //    so the merge is visible on the client record (non-fatal).
  try {
    await addDoc(collection(db, 'clients', survivor.cid, 'notes'), {
      body:
        `Merged duplicate client ${duplicate.clientNo} (${duplicate.name}) ` +
        `into this record — ${dupProjects.length} project` +
        `${dupProjects.length === 1 ? '' : 's'} re-pointed` +
        `${contactCarried ? ', contact details carried over' : ''}. ` +
        `The duplicate was moved to Trash.`,
      author: actorName,
      kind: 'client_merge',
      at: serverTimestamp(),
    });
  } catch {
    /* the merge itself succeeded — a missing audit note is non-fatal */
  }

  return {
    projectsMoved: dupProjects.length,
    contactCarried,
    trashedClientId: duplicate.cid,
  };
}

/* ════════════════════ Module 2 — Participant matrix (AD-009) ════════════════

addParticipant / removeParticipant / seedParticipantsFromLegacy implement the
per-project participant matrix. The primary_client contactId MUST stay in sync
with project.clientId on every write (backward-compat shortcut).

updateContactPersonData / savePersonData / saveAstroChartRef / getPersonDataForReport
are the Person Data Hub seams (AD-008). saveAstroChartRef and getPersonDataForReport
are marked with ASTRO_ENGINE_SEAM / REPORT_ENGINE_SEAM comments so the next dispatch
can grep for them.
══════════════════════════════════════════════════════════════════════════════ */

export async function addParticipant(
  pid: string,
  entry: ParticipantEntry,
): Promise<void> {
  // Read current participants, append new entry, write back
  const ref = doc(db, 'projects', pid);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Project ${pid} not found`);
  const existing: ParticipantEntry[] = Array.isArray(snap.data().participants)
    ? (snap.data().participants as ParticipantEntry[])
    : [];
  // Prevent duplicate contactId + role combo
  const filtered = existing.filter(
    (e) => !(e.contactId === entry.contactId && e.role === entry.role),
  );
  await updateDoc(ref, {
    participants: [...filtered, entry],
    updatedAt: serverTimestamp(),
  });
}

export async function removeParticipant(
  pid: string,
  contactId: string,
  role?: string,
): Promise<void> {
  const ref = doc(db, 'projects', pid);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Project ${pid} not found`);
  const existing: ParticipantEntry[] = Array.isArray(snap.data().participants)
    ? (snap.data().participants as ParticipantEntry[])
    : [];
  // Guard: cannot remove primary_client (it would orphan the project)
  const toRemove = existing.find(
    (e) => e.contactId === contactId && (!role || e.role === role),
  );
  if (toRemove?.role === 'primary_client') {
    throw new Error(
      'Cannot remove the primary client from a project. ' +
        'Replace them with a different primary client first.',
    );
  }
  await updateDoc(ref, {
    participants: existing.filter(
      (e) => !(e.contactId === contactId && (!role || e.role === role)),
    ),
    updatedAt: serverTimestamp(),
  });
}

/**
 * PURE — seed the participants[] from the legacy clientId / partnerId shortcut
 * fields. Called on first render of a project that has no participants[] yet.
 * Returns the seeded array; the caller writes it back via addParticipant or
 * a direct updateDoc.
 */
export function seedParticipantsFromLegacy(project: {
  clientId: string;
  partnerId?: string | null;
  participants?: ParticipantEntry[];
}): ParticipantEntry[] {
  if (project.participants && project.participants.length > 0) {
    return project.participants;
  }
  const seeded: ParticipantEntry[] = [];
  if (project.clientId) {
    seeded.push({ contactId: project.clientId, role: 'primary_client' });
  }
  if (project.partnerId) {
    seeded.push({ contactId: project.partnerId, role: 'partner' });
  }
  return seeded;
}

/**
 * Write personal data (DOB / TOB / POB / Prakriti) to a contacts/{contactId}
 * doc. This is what the intake form and the Contact detail screen call.
 *
 * // ASTRO_ENGINE_SEAM — the Astro engine also calls this to write birth data
 * // before computing a chart.
 */
export async function updateContactPersonData(
  contactId: string,
  data: {
    dob?: string;
    tob?: string;
    pob?: PlaceOfBirth;
    prakriti?: string;
    /** Per-person health context (astro-subject step ③). Portal-side PII. */
    health?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      pin?: string;
      country?: string;
    } | null;
    referredBy?: { contactId: string; name: string } | null;
  },
): Promise<void> {
  // ASTRO_ENGINE_SEAM
  // Firestore rejects `undefined` field values (the client is NOT initialised
  // with `ignoreUndefinedProperties`). The Contact editor builds `pob`/`address`
  // objects whose blank sub-fields are `undefined` (e.g. only `city` filled) —
  // passing those straight through made `updateDoc()` throw
  // "Unsupported field value: undefined". Strip undefined keys from the nested
  // data objects before writing. `null` is preserved (an intentional clear).
  const out: Record<string, unknown> = {};
  if (typeof data.dob === 'string') out.dob = data.dob;
  if (typeof data.tob === 'string') out.tob = data.tob;
  if (data.pob && typeof data.pob === 'object') out.pob = pruneUndefined(data.pob);
  if (typeof data.prakriti === 'string') out.prakriti = data.prakriti;
  if (typeof data.health === 'string') out.health = data.health;
  if (data.address !== undefined) out.address = data.address ? pruneUndefined(data.address) : null;
  if (data.referredBy !== undefined) out.referredBy = data.referredBy ?? null;
  out.updatedAt = serverTimestamp();
  await updateDoc(doc(db, 'clients', contactId), out);
}

/** Alias kept for call-site clarity. */
export const savePersonData = updateContactPersonData;

/**
 * Write the phones[] array to a clients/{contactId} doc. Called from
 * ClientDetail after migratePhoneField seeds the legacy `phone` string into
 * the new array form (WA amendment, 2026-06-17). Accepts the full phones
 * array (already de-duped and whatsApp-flagged by migratePhoneField).
 */
export async function savePhones(
  contactId: string,
  phones: Array<{ number: string; label?: string; whatsApp?: boolean }>,
): Promise<void> {
  await updateDoc(doc(db, 'clients', contactId), {
    phones,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Write the emails[] array to a clients/{contactId} doc (P1 §6, datamap #4a).
 * Mirrors `savePhones` — the contact-side multi-email editor is the authoritative
 * source; the caller also mirrors the primary address into the legacy flat
 * `email` field (via `updateClient`) for back-compat readers.
 */
export async function saveEmails(
  contactId: string,
  emails: Array<{ address: string; label?: string }>,
): Promise<void> {
  await updateDoc(doc(db, 'clients', contactId), {
    emails,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Write the full `roleTags[]` array to a clients/{contactId} doc (P1 §6 —
 * contact-side role assignment). Replaces the array wholesale so the editor's
 * checkbox set is the source of truth (unlike the additive `addContactRoleTag`
 * used by the AD-040 intake path). Values are the controlled
 * `ASSIGNABLE_CONTACT_ROLES` (never free-text).
 */
export async function saveRoleTags(
  contactId: string,
  roleTags: string[],
): Promise<void> {
  await updateDoc(doc(db, 'clients', contactId), {
    roleTags,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Write the astro chart reference back to the Contact after the Astro engine
 * has computed + stored a chart. Sets both astroChartRef and astroChartCached.
 *
 * // ASTRO_ENGINE_SEAM — called exclusively by the Astro engine after chart computation.
 */
export async function saveAstroChartRef(
  contactId: string,
  chartRef: string,
): Promise<void> {
  // ASTRO_ENGINE_SEAM
  await updateDoc(doc(db, 'clients', contactId), {
    astroChartRef: chartRef,
    astroChartCached: true,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Read person data for the report engine — fetches DOB/TOB/POB/Prakriti +
 * astroChartRef from a Contact in one read. Called by the report factory
 * (Module 6 — Output Factory) when assembling a family report.
 *
 * // REPORT_ENGINE_SEAM — called by the report engine (Module 6 / Output Factory).
 */
export async function getPersonDataForReport(contactId: string): Promise<{
  dob?: string;
  tob?: string;
  pob?: PlaceOfBirth;
  prakriti?: string;
  astroChartRef?: string;
}> {
  // REPORT_ENGINE_SEAM
  const snap = await getDoc(doc(db, 'clients', contactId));
  if (!snap.exists()) return {};
  const d = snap.data();
  return {
    dob: typeof d.dob === 'string' ? d.dob : undefined,
    tob: typeof d.tob === 'string' ? d.tob : undefined,
    pob: hydratePob(d.pob),
    prakriti: typeof d.prakriti === 'string' ? d.prakriti : undefined,
    astroChartRef: typeof d.astroChartRef === 'string' ? d.astroChartRef : undefined,
  };
}

/**
 * PURE — defensively hydrate a stored `pob` map into the structured
 * `PlaceOfBirth` shape. City/state/country are the source of truth and default
 * to '' (never undefined) so the Contact card can bind them directly without
 * re-parsing `name`. Returns undefined when there is no pob object at all.
 */
export function hydratePob(v: unknown): PlaceOfBirth | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const p = v as Record<string, unknown>;
  const s = (x: unknown): string => (typeof x === 'string' ? x : '');
  const num = (x: unknown): number | undefined =>
    typeof x === 'number' && Number.isFinite(x) ? x : undefined;
  const out: PlaceOfBirth = {
    name: s(p.name),
    city: s(p.city),
    state: s(p.state),
    country: s(p.country),
    tz: s(p.tz) || 'Asia/Kolkata',
  };
  // Build A — emit lat/lon only when present (a partial POB stays partial, no
  // 0,0 fabrication). LEGACY-lng READ HYDRATION: a doc written before the rename
  // still carries `lng`; read it into `lon` so no existing Contact loses
  // longitude. One-way (read only) — we write only `lon` going forward.
  const lat = num(p.lat);
  if (lat !== undefined) out.lat = lat;
  const lon = num(p.lon) ?? num(p.lng);
  if (lon !== undefined) out.lon = lon;
  return out;
}

/**
 * PURE — find pairs of contacts that are likely duplicates.
 * Flags pairs sharing a normalised phone OR near-identical names (same after
 * lowercase+trim). Self-pairs excluded. Each pair appears only once.
 */
export function findDuplicatePairs(
  contacts: Array<{
    cid: string;
    name: string;
    clientNo: string;
    phone?: string;
    phones?: Array<{ number: string }>;
  }>,
): Array<[typeof contacts[0], typeof contacts[0]]> {
  function normPhone(p: string): string {
    return p.replace(/[\s\-().+]/g, '').replace(/^91/, '').replace(/^0/, '');
  }
  function normName(n: string): string {
    return n.trim().toLowerCase().replace(/\s+/g, ' ');
  }

  const pairs: Array<[typeof contacts[0], typeof contacts[0]]> = [];
  const seen = new Set<string>();

  for (let i = 0; i < contacts.length; i++) {
    for (let j = i + 1; j < contacts.length; j++) {
      const a = contacts[i], b = contacts[j];
      const pairKey = [a.cid, b.cid].sort().join('::');
      if (seen.has(pairKey)) continue;

      // Phone match
      const phonesA = [
        ...(a.phones ?? []).map((p) => p.number),
        ...(a.phone ? [a.phone] : []),
      ].map(normPhone).filter(Boolean);
      const phonesB = [
        ...(b.phones ?? []).map((p) => p.number),
        ...(b.phone ? [b.phone] : []),
      ].map(normPhone).filter(Boolean);
      const phoneMatch = phonesA.some((p) => phonesB.includes(p));

      // Name match
      const nameMatch = normName(a.name) === normName(b.name) && normName(a.name) !== '';

      if (phoneMatch || nameMatch) {
        pairs.push([a, b]);
        seen.add(pairKey);
      }
    }
  }
  return pairs;
}

/* ════════════════════ WA × Contact seam ═══════════════════════════════════
 *
 * WA_TEAMINBOX_SEAM — Module 2 WA amendment (2026-06-17).
 *
 * `resolveContactByWhatsApp` is called by the WA Team Inbox to look up an
 * inbound sender phone number against the contacts list. It works on the
 * ALREADY-LOADED contacts array (from `listenClients`), so there is NO extra
 * Firestore round-trip — the contacts stream is already live in the inbox UI.
 *
 * Matching rules:
 *  1. phones[] entries with `whatsApp:true` are searched first (explicit flag).
 *  2. The legacy single-string `phone` field is the fallback (all pre-Module-2
 *     client docs that have never been saved through the new editor, plus the
 *     migrated entries in the phones[] array that have whatsApp:true seeded by
 *     `migratePhoneField`).
 *  Phone normalisation strips +91 / 091 / leading 0 / spaces / dashes —
 *  identical logic to `findContactByChannel` in lib/leads.ts.
 * ──────────────────────────────────────────────────────────────────────── */

function _normWaPhone(p: string): string {
  // Digits only. Contacts are saved in every shape a human types
  // ("+91 94062 92000", "094062-92000", "919406292000") while a conversation
  // always carries the canonical all-digit form. The old version stripped
  // "+91" but NOT a bare leading "91", so "+919406292000" became 9406292000
  // while the conversation's "919406292000" stayed 12 digits — a saved contact
  // could never match its own WhatsApp thread, and the inbox showed a bare
  // number with a "+ Create Contact" link that would have made a duplicate
  // (owner report, 2026-08-02).
  let n = p.replace(/\D/g, '');
  n = n.replace(/^0+/, '');
  // India country code, with or without the plus the user happened to type.
  if (n.length > 10 && n.startsWith('91')) n = n.slice(2);
  return n;
}

/**
 * PURE — look up an inbound WhatsApp sender by phone number against the
 * already-loaded contacts list. Returns the first matching `LiveClient` or
 * `null` when no match is found.
 *
 * Match priority:
 *  1. phones[] entries that have `whatsApp:true`
 *  2. The legacy single-string `phone` field (treated as a WhatsApp number
 *     for any client doc that has not yet been migrated / explicitly flagged)
 *
 * Called by the WA Team Inbox row/header render — no Firestore call needed.
 */
// WA_TEAMINBOX_SEAM
export function resolveContactByWhatsApp(
  phone: string,
  contacts: LiveClient[],
): LiveClient | null {
  const norm = _normWaPhone(phone);
  if (!norm) return null;
  for (const c of contacts) {
    // Check phones[] entries that are explicitly flagged as WhatsApp
    const entries = c.phones ?? [];
    for (const e of entries) {
      if (e.whatsApp && _normWaPhone(e.number) === norm) return c;
    }
    // Fallback: the legacy single phone field (pre-Module-2 docs that still
    // only have `phone`, and have not been migrated yet). These are treated as
    // WhatsApp numbers because AV has always communicated via WhatsApp.
    if (c.phone && !entries.length && _normWaPhone(c.phone) === norm) return c;
  }
  return null;
}

// ─── Visibility Overrides — Phase 4, Module 4 (AD-013) ───────────────────
//
// Per-project visibility overrides are stored as a sparse map at
//   `projects/{pid}.visibilityOverrides: { [role]: { [section]: boolean } }`
// These are applied on top of the global matrix in `canSee()`.

/**
 * Set a single role×section override on a project.
 * Uses dot-notation so all other overrides are untouched.
 */
export async function setProjectVisibilityOverride(
  projectId: string,
  role: PortalRole,
  section: PortalSection,
  value: boolean,
  actorUid: string,
): Promise<void> {
  await updateDoc(doc(db, 'projects', projectId), {
    [`visibilityOverrides.${role}.${section}`]: value,
    _visibilityUpdatedBy: actorUid,
    _visibilityUpdatedAt: new Date().toISOString(),
  });
}

/**
 * Clear a single role×section override (removes the field — falls back to global).
 */
export async function clearProjectVisibilityOverride(
  projectId: string,
  role: PortalRole,
  section: PortalSection,
  actorUid: string,
): Promise<void> {
  await updateDoc(doc(db, 'projects', projectId), {
    [`visibilityOverrides.${role}.${section}`]: deleteField(),
    _visibilityUpdatedBy: actorUid,
    _visibilityUpdatedAt: new Date().toISOString(),
  });
}

/**
 * Remove all per-project visibility overrides (wipes `visibilityOverrides` map).
 */
export async function clearAllProjectOverrides(
  projectId: string,
  actorUid: string,
): Promise<void> {
  await updateDoc(doc(db, 'projects', projectId), {
    visibilityOverrides: deleteField(),
    _visibilityUpdatedBy: actorUid,
    _visibilityUpdatedAt: new Date().toISOString(),
  });
}
